import { useState, useEffect, useContext } from 'react';
import { CircularProgress, Modal, Tooltip, ToggleButton, ToggleButtonGroup, Tabs, Tab } from '@mui/material';
import { Close, Edit, Check, ViewModule, ViewSidebar, ViewWeek, ViewArray, Add, DragIndicator, Lock, LockOpen } from '@mui/icons-material';
import Select from 'react-select';
import { twMerge } from 'tailwind-merge';
import { WarehouseType, WarehouseZoneType, DockType as ImportedDockType } from '../../types/Facility';
import { isAdmin } from '../../utils/roleHelper';
import { UserContext } from '../../App';
import { getAuthToken } from '../../utils/cookieUtils';
// Add GraphQL API URL constant
const GRAPHQL_API_URL = process.env.REACT_APP_GRAPHQL_API || 'http://localhost:4000/api/graphql';

// Add GraphQL mutations for warehouse zones
const UPDATE_WAREHOUSE_ZONE_MUTATION = `
  mutation UpdateWarehouseZone($uuid: String!, $input: WarehouseZoneUpdateInput!) {
    update_warehouse_zone(uuid: $uuid, input: $input) {
      uuid
      name
      facility_id
      description
      parent_zone_uuid
      depth
      configuration
      created_at
      updated_at
    }
  }
`;

const ADD_WAREHOUSE_ZONE_MUTATION = `
  mutation AddWarehouseZone($input: WarehouseZoneInput!) {
    add_warehouse_zone(input: $input) {
      uuid
      name
      facility_id
      description
      parent_zone_uuid
      depth
      configuration
      created_at
      updated_at
    }
  }
`;

// Add GraphQL mutations for docks
const UPDATE_DOCK_MUTATION = `
  mutation UpdateDock($uuid: String!, $input: DockUpdateInput!) {
    update_dock(uuid: $uuid, input: $input) {
      uuid
      name
      facility_id
      location_id
      order_number
      warehouse_zone_uuid
      configuration
      created_at
      updated_at
    }
  }
`;

const ADD_DOCK_MUTATION = `
  mutation AddDock($input: DockInput!) {
    add_dock(input: $input) {
      uuid
      name
      facility_id
      location_id
      order_number
      warehouse_zone_uuid
      configuration
      created_at
      updated_at
    }
  }
`;

// Add GraphQL mutation for facility updates
const UPDATE_FACILITY_MUTATION = `
  mutation UpdateFacility($id: ID!, $input: FacilityUpdateInput!) {
    update_facility(id: $id, input: $input) {
      id
      name
      configuration
    }
  }
`;

// Types for dock status and layouts
type DockStatus = 'available' | 'in-use' | 'unavailable';
type LayoutType = 'single-sided' | 'double-sided' | 'u-shaped' | 'square';

// Add color configuration types
type TimerColorConfig = {
  warning: string;
  critical: string;
  good: string;
  warningThreshold: number;
  criticalThreshold: number;
  textColor: string;
};

type CardColorConfig = {
  default: string;
  oneWarning: string;
  twoWarnings: string;
  threeWarnings: string;
  oneCritical: string;
  twoCriticals: string;
  threeCriticals: string;
  mixed: string;
  textColor: string;
};

type ColorConfig = {
  mdt: TimerColorConfig;
  detention: TimerColorConfig;
  dockTimer: TimerColorConfig;
  card: CardColorConfig;
};

// Maintainable list of dock types for easy expansion
const DOCK_TYPES = [
  { value: 'inbound', label: 'Inbound', color: '#3b82f6' }, // blue
  { value: 'outbound', label: 'Outbound', color: '#f97316' }, // orange
  { value: 'mixed', label: 'Mixed', color: '#8b5cf6' }, // purple
  { value: 'maintenance', label: 'Maintenance Only', color: '#6b7280' }, // gray
  { value: 'staging', label: 'Staging', color: '#10b981' }, // green
  { value: 'unassigned', label: 'Unassigned', color: '#9ca3af' }, // gray-400
] as const;

type DockType = typeof DOCK_TYPES[number]['value'];

// Define dock positions for layout configuration
type DockPosition = 'top' | 'bottom' | 'left' | 'right';

// Base dock type with common properties
type BaseDock = {
  id: string;
  number: string;
  status: DockStatus;
  type: DockType;
  zoneId: string;
  locationId?: string; // Location ID for the dock
  facilityId?: string; // Facility ID for the dock
  configuration?: any; // Configuration object or JSON string
  order_number?: number; // Add order_number for drag and drop ordering
  position?: DockPosition; // Add position for layout-based configuration
  mdt?: string; // MDT timer value
  detention?: string; // Detention timer value
  dock_timer?: string; // Dock timer value
  notes?: string; // Notes for the dock door
};

// Zone type for warehouse organization with dock type configuration
type WarehouseZone = {
  id: string;
  name: string;
  color: string; // CSS color for visual indication
  description?: string;
  defaultDockType: DockType; // Default dock type for this zone
};

// Define props for the WarehouseManagement component
interface WarehouseManagementProps {
  warehouse?: WarehouseType | null;
  facility?: any; // Add facility prop to get facility ID for mutations
  selectedWarehouseId?: string | null;
  onWarehouseChange?: (warehouseId: string) => void;
  onCancel?: () => void;
}

const WarehouseManagement = ({ warehouse, facility, selectedWarehouseId, onWarehouseChange, onCancel }: WarehouseManagementProps = {}) => {
  // @ts-ignore
  const { user } = useContext(UserContext);
  const userIsAdmin = isAdmin(user);
  const [loading, setLoading] = useState(false);
  const [docks, setDocks] = useState<BaseDock[]>([]);
  const [selectedDock, setSelectedDock] = useState<BaseDock | null>(null);
  const [showDockModal, setShowDockModal] = useState(false);
  const [showWarehouseEditModal, setShowWarehouseEditModal] = useState(false);
  const [showDockEditModal, setShowDockEditModal] = useState(false);
  const [showZoneEditModal, setShowZoneEditModal] = useState(false);
  const [showDockStatusModal, setShowDockStatusModal] = useState(false);
  const [editingDock, setEditingDock] = useState<BaseDock | null>(null);
  const [originalDockStatus, setOriginalDockStatus] = useState<DockStatus | null>(null);
  const [dockNotesValue, setDockNotesValue] = useState('');
  const [dockLocks, setDockLocks] = useState<any[]>([]);
  const [loadingLocks, setLoadingLocks] = useState(false);
  const [clearingLock, setClearingLock] = useState(false);
  const [allFacilityLocks, setAllFacilityLocks] = useState<any[]>([]);
  const [editingZone, setEditingZone] = useState<WarehouseZone | null>(null);
  const [statusFilter, setStatusFilter] = useState<DockStatus | 'all'>('all');
  const [layout, setLayout] = useState<LayoutType>('double-sided');
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [configModalTab, setConfigModalTab] = useState(0);
  const [colorConfig, setColorConfig] = useState<ColorConfig>({
    mdt: {
      warning: '#FFEDD5', // orange-100
      critical: '#FEE2E2', // red-100
      good: '#D1FAE5',    // green-100
      warningThreshold: 60, // minutes
      criticalThreshold: 45, // minutes
      textColor: '#000000', // black
    },
    detention: {
      warning: '#FFEDD5', // orange-100
      critical: '#FEE2E2', // red-100
      good: '#D1FAE5',    // green-100
      warningThreshold: 60, // minutes
      criticalThreshold: 45, // minutes
      textColor: '#000000', // black
    },
    dockTimer: {
      warning: '#FFEDD5', // orange-100
      critical: '#FEE2E2', // red-100
      good: '#D1FAE5',    // green-100
      warningThreshold: 45, // minutes
      criticalThreshold: 90, // minutes
      textColor: '#000000', // black
    },
    card: {
      default: '#FFFFFF',   // white (default)
      oneWarning: '#FFFBEB', // yellow-50
      twoWarnings: '#FEF3C7', // yellow-100
      threeWarnings: '#FDE68A', // yellow-200
      oneCritical: '#FEF2F2', // red-50
      twoCriticals: '#FEE2E2', // red-100
      threeCriticals: '#FECACA', // red-200
      mixed: '#FDF2F8',     // pink-50 (for mixed warning/critical)
      textColor: '#000000', // black
    }
  });
  
  // State for tracking zone changes to be saved
  const [pendingZoneChanges, setPendingZoneChanges] = useState<Record<string, any>>({});
  const [pendingDockChanges, setPendingDockChanges] = useState<Record<string, any>>({});
  const [savingChanges, setSavingChanges] = useState(false);
  // Occupancy awareness for config UI (freshly fetched on Edit Layout)
  const [occupiedDockIds, setOccupiedDockIds] = useState<Set<string>>(new Set());
  // Occupancy hinting: mark docks that are in-use to visually lock config interactions
  const isDockOccupied = (dock: BaseDock) => dock.status === 'in-use';

  // State for tracking facility changes
  const [pendingFacilityChanges, setPendingFacilityChanges] = useState<Record<string, any>>({});

  // Add drag and drop state
  const [draggedDock, setDraggedDock] = useState<BaseDock | null>(null);
  const [dragOverZone, setDragOverZone] = useState<string | null>(null);

  // Define zones for warehouse organization
  const [zones, setZones] = useState<WarehouseZone[]>([
    { id: 'zone-1', name: 'Receiving', color: '#3b82f6', description: 'Inbound dock area', defaultDockType: 'inbound' }, // blue
    { id: 'zone-2', name: 'Shipping', color: '#f97316', description: 'Outbound dock area', defaultDockType: 'outbound' }, // orange
    { id: 'zone-3', name: 'Mixed Operations', color: '#10b981', description: 'Flexible dock area', defaultDockType: 'mixed' } // green
  ]);

  // Track which zones came from the API (have valid UUIDs)
  const [originalZones, setOriginalZones] = useState<string[]>([]);

  // Add useEffect hook for initial data loading - now depends on both warehouse AND facility
  useEffect(() => {
    setLoading(true);
    
    // If warehouse data is provided, use it to initialize the component
    if (warehouse) {
      processWarehouseData(warehouse, facility);
      
      // Fetch facility locks
      if (facility?.id) {
        fetchAllFacilityLocks(facility.id);
      }
    } else {
      // No warehouse data provided - don't generate mock data
      setDocks([]);
      setLoading(false);
    }
  }, [warehouse, facility]); // Now depends on both warehouse and facility

  // Reassign dock positions when layout changes (fixes reload issue)
  useEffect(() => {
    if (docks.length > 0) {
      const reassignedDocks = assignDockPositionsByLayout(docks, layout);
      setDocks(reassignedDocks);
    }
  }, [layout]); // Trigger when layout changes

  // Process warehouse data from props
  const processWarehouseData = (warehouseData: WarehouseType, facilityData?: any) => {
    try {
      if (!warehouseData) {
        throw new Error('No warehouse data provided');
      }

      // First, set the layout and color config from facility configuration if available
      if (facilityData?.configuration) {
        try {
          const config = typeof facilityData.configuration === 'string' 
            ? JSON.parse(facilityData.configuration) 
            : facilityData.configuration;
          
          // Set layout
          if (config.warehouse?.layout) {
            setLayout(config.warehouse.layout as LayoutType);
          }
          
          // Set color configuration
          if (config?.dock?.colorConfig) {
            setColorConfig(config.dock.colorConfig);
          }
        } catch (error) {
          console.error('Error parsing facility configuration:', error);
        }
      }
      
      // Extract zones from the warehouse data (include zones even if they don't have docks yet)
      const extractedZones: WarehouseZone[] = warehouseData.warehouseZones
        .map((zone, index) => ({
          id: zone.zoneUuid,
          name: zone.zoneName,
          description: zone.zoneDescription || undefined, // Handle missing description
          color: zone.configuration?.color || getColorForZone(index), // Use color from configuration or fallback
          defaultDockType: zone.configuration?.dockType || 'unassigned' // Use default dock type from configuration or fallback
        }));
      
      setZones(extractedZones);
      setOriginalZones(extractedZones.map(z => z.id)); // Track original zone IDs
      
      // Reset pending changes when loading new data
      setPendingZoneChanges({});
      setPendingDockChanges({});
      setPendingFacilityChanges({});
      
      // Convert docks to the format expected by the component
      const extractedDocks: BaseDock[] = [];
      
      // Add docks from warehouse zones
      warehouseData.warehouseZones.forEach(zone => {
        if (zone.docks && zone.docks.length > 0) {
          zone.docks.forEach(dock => {
            // Handle null status by defaulting to 'available' - check configuration first, then dockStatus
            const status = (dock.configuration?.status as DockStatus) || (dock.dockStatus as DockStatus) || 'available';
            
            // Get dock type from dock configuration first, then zone configuration, then dock property
            const zoneConfig = zone.configuration || {};
            const dockType: DockType = (dock.configuration?.type as DockType) || zoneConfig.dockType || dock.dockType || 'unassigned';
            
            // Log for debugging
            console.log(`Processing dock ${dock.dockName} in zone ${zone.zoneName}:`, {
              dockConfigType: dock.configuration?.type,
              dockConfigStatus: dock.configuration?.status,
              zoneDockType: zoneConfig.dockType,
              dockDockType: dock.dockType,
              dockStatus: dock.dockStatus,
              finalDockType: dockType,
              finalStatus: status,
              dockConfig: dock.configuration,
              zoneConfig
            });
            
            extractedDocks.push({
              id: dock.dockUuid,
              number: dock.dockName,
              status: status,
              type: dockType,
              zoneId: zone.zoneUuid,
              order_number: dock.order_number,
              position: dock.configuration?.position as DockPosition, // Get position from configuration if available
              mdt: dock.mdt,
              detention: dock.detention,
              dock_timer: dock.dock_timer,
              notes: dock.configuration?.notes // Get notes from configuration if available
            });
          });
        }
      });
      
      // Auto-assign positions to docks that don't have them based on current layout
      const docksWithPositions = assignDockPositionsByLayout(extractedDocks, layout);
      setDocks(docksWithPositions);
      setLoading(false);
    } catch (error) {
      console.error('Error processing warehouse data:', error);
      // Fallback to empty state
      setDocks([]);
      setZones([]);
      setLoading(false);
    }
  };
  
  // Helper function to generate colors for zones
  const getColorForZone = (index: number): string => {
    const colors = [
      '#3b82f6', // blue
      '#f97316', // orange
      '#10b981', // green
      '#ef4444', // red
      '#8b5cf6', // purple
      '#f59e0b', // amber
      '#ec4899'  // pink
    ];
    
    return colors[index % colors.length];
  };

  const handleDockClick = async (dock: BaseDock) => {
    // Open status change modal
    setSelectedDock(dock);
    setOriginalDockStatus(dock.status);
    setDockNotesValue(dock.notes || '');
    setShowDockStatusModal(true);
    
    // Fetch locks for this dock
    await fetchDockLocks(dock.id);
  };

  const handleDockModalClose = () => {
    setShowDockModal(false);
    setSelectedDock(null);
  };

  const handleDockStatusModalClose = () => {
    setShowDockStatusModal(false);
    setSelectedDock(null);
    setOriginalDockStatus(null);
    setDockNotesValue('');
  };

  const handleDockStatusSave = async () => {
    if (!selectedDock) return;

    const statusChanged = selectedDock.status !== originalDockStatus;
    const notesChanged = dockNotesValue !== (selectedDock.notes || '');

    if (statusChanged) {
      const confirmed = window.confirm('Are you sure you want to change the status?');
      if (!confirmed) return;
    }

    if (!statusChanged && !notesChanged) {
      handleDockStatusModalClose();
      return;
    }

    try {
      setLoading(true);

      const authToken = getAuthToken();

      if (!authToken) {
        throw new Error('Authentication required');
      }

      // Update local state
      const updatedDock = { ...selectedDock, notes: dockNotesValue || undefined };
      setDocks(docks.map(dock =>
        dock.id === selectedDock.id ? updatedDock : dock
      ));

      // Prepare configuration
      const currentConfig = typeof selectedDock.configuration === 'string'
        ? JSON.parse(selectedDock.configuration)
        : selectedDock.configuration || {};

      const updatedConfig = {
        ...currentConfig,
        status: selectedDock.status,
        notes: dockNotesValue || null
      };

      // Execute mutation
      const input = {
        name: selectedDock.number,
        location_id: selectedDock.locationId || facility?.warehouse_id,
        order_number: selectedDock.order_number ? String(selectedDock.order_number) : undefined,
        warehouse_zone_uuid: selectedDock.zoneId,
        configuration: JSON.stringify(updatedConfig)
      };

      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: UPDATE_DOCK_MUTATION,
          variables: {
            uuid: selectedDock.id,
            input
          }
        })
      });

      const result = await response.json();

      if (result.errors) {
        console.error('Error updating dock status:', result.errors);
        throw new Error(result.errors[0].message);
      }

      console.log('Dock status updated:', result.data.update_dock);
      alert('Status updated successfully!');
      handleDockStatusModalClose();
      
    } catch (error: any) {
      console.error('Error updating dock status:', error);
      alert(`Failed to update status: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Fetch all facility locks
  const fetchAllFacilityLocks = async (facilityId: string) => {
    try {
      const authToken = getAuthToken();

      if (!authToken) return;

      const facilityLocksQuery = `
        query {
          facilityLocks(facilityId: "${facilityId}") {
            reason
            resource_uuid
            model_name
            operation_type
            created_by
            task_uuid
            created_at
            expires_at
            namespace
            facility_id
            resource {
              ... on YardSpotObject {
                __typename
                uuid
                name
              }
              ... on DockObject {
                __typename
                uuid
                name
              }
              ... on Trailer {
                __typename
                uuid
                trailerNumber
              }
            }
          }
        }
      `;

      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: facilityLocksQuery
        })
      });

      const result = await response.json();
      
      if (result.data && result.data.facilityLocks) {
        setAllFacilityLocks(result.data.facilityLocks);
      }
    } catch (error) {
      console.error('Error fetching facility locks:', error);
    }
  };

  // Fetch locks for a specific dock (includes both location and trailer locks)
  const fetchDockLocks = async (dockId: string) => {
    setLoadingLocks(true);
    try {
      // Get locks on the location itself
      const locationLocks = allFacilityLocks.filter(lock => lock.resource_uuid === dockId);
      
      // Note: We only show location locks here. Trailer locks are indirectly shown
      // since a locked trailer at a dock prevents operations on that dock.
      // To show trailer locks, we'd need to match trailers to docks from warehouse data.
      
      setDockLocks(locationLocks);
    } catch (error) {
      console.error('Error fetching dock locks:', error);
    } finally {
      setLoadingLocks(false);
    }
  };

  // Clear a lock
  const handleClearLock = async (resourceUuid: string, taskUuid: string | null) => {
    setClearingLock(true);
    try {
      const authToken = getAuthToken();

      if (!authToken) {
        throw new Error('Authentication required');
      }

      const clearLockMutation = `
        mutation ReleaseTaskLocks($taskUuid: String!) {
          releaseTaskLocks(taskUuid: $taskUuid) {
            success
            message
          }
        }
      `;

      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: clearLockMutation,
          variables: { taskUuid: taskUuid || resourceUuid }
        })
      });

      const result = await response.json();
      
      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      alert('Lock cleared successfully!');
      
      // Reload the page to refresh all data
      window.location.reload();
    } catch (error: any) {
      console.error('Error clearing lock:', error);
      alert(`Failed to clear lock: ${error.message}`);
      setClearingLock(false);
    }
  };

  // Check if a dock has locks
  const dockHasLock = (dockId: string) => {
    return allFacilityLocks.some(lock => lock.resource_uuid === dockId);
  };

  const handleStatusChange = (status: DockStatus) => {
    if (selectedDock) {
      const updatedDocks = docks.map(dock => 
        dock.id === selectedDock.id ? { ...dock, status } : dock
      );
      setDocks(updatedDocks);
      setSelectedDock({ ...selectedDock, status });
    }
  };

  const handleLayoutChange = (event: React.MouseEvent<HTMLElement>, newLayout: LayoutType) => {
    if (newLayout !== null) {
      setLayout(newLayout);
      
      // Automatically assign dock positions based on layout
      const updatedDocks = assignDockPositionsByLayout(docks, newLayout);
      setDocks(updatedDocks);
      
      // Track dock position changes for API updates
      updatedDocks.forEach(dock => {
        if (dock.id.includes('-') && !dock.id.startsWith('dock-')) {
          setPendingDockChanges(prev => ({
            ...prev,
            [dock.id]: {
              ...prev[dock.id],
              configuration: {
                ...(prev[dock.id]?.configuration || {}),
                position: dock.position
              }
            }
          }));
        }
      });
      
      // Track the facility configuration change
      if (facility) {
        setPendingFacilityChanges(prev => ({
          ...prev,
          configuration: {
            ...(prev.configuration || {}),
            warehouse: {
              ...(prev.configuration?.warehouse || {}),
              layout: newLayout,
              lastUpdated: new Date().toISOString()
            }
          }
        }));
      }
    }
  };

  // Helper function to assign dock positions based on layout
  const assignDockPositionsByLayout = (docksToUpdate: BaseDock[], layoutType: LayoutType): BaseDock[] => {
    const totalDocks = docksToUpdate.length;
    if (totalDocks === 0) return docksToUpdate;

    return docksToUpdate.map((dock, index) => {
      let position: DockPosition;
      
      switch (layoutType) {
        case 'single-sided':
          // All docks on top
          position = 'top';
          break;
          
        case 'double-sided':
          // Split between top and bottom
          position = index < Math.ceil(totalDocks / 2) ? 'top' : 'bottom';
          break;
          
        case 'u-shaped':
          // Distribute across top, left, and right
          const thirdSize = Math.ceil(totalDocks / 3);
          if (index < thirdSize) {
            position = 'top';
          } else if (index < thirdSize * 2) {
            position = 'left';
          } else {
            position = 'right';
          }
          break;
          
        case 'square':
          // Distribute across all four sides
          const quarterSize = Math.ceil(totalDocks / 4);
          if (index < quarterSize) {
            position = 'top';
          } else if (index < quarterSize * 2) {
            position = 'right';
          } else if (index < quarterSize * 3) {
            position = 'bottom';
          } else {
            position = 'left';
          }
          break;
          
        default:
          position = 'top';
      }
      
      return {
        ...dock,
        position
      };
    });
  };

  // Load current dock occupancy for lock hints
  const loadWarehouseOccupancy = async () => {
    try {
      const facilityId = facility?.id || warehouse?.facilityId;
      if (!facilityId) return;

      const authToken = getAuthToken();

      // Mirror DockManagement/dockDataFetch combined fields to assess occupancy accurately
      const occupancyQuery = `
        query GetWarehouseOccupancy {
          warehouse(facilityId: "${facilityId}") {
            warehouseZones {
              zoneUuid
              docks {
                dockUuid
                dockName
                dockStatus
                order_number
                trailer_name
                trailer_uuid
                carrier_name
                configuration
                trailer {
                  active_lineage { uuid }
                  status { name }
                }
                shipment { id referenceNumber direction }
                driver { id firstName lastName }
              }
            }
          }
        }
      `;

      const resp = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
        body: JSON.stringify({ query: occupancyQuery })
      });
      const data = await resp.json();
      if (data.errors) return;

      const next = new Set<string>();
      data?.data?.warehouse?.warehouseZones?.forEach((zone: any) => {
        (zone.docks || []).forEach((dock: any) => {
          const hasTrailer = !!dock.trailer_name || !!dock.trailer_uuid || !!dock.trailer?.status?.name;
          const occupiedByStatus = String(dock.dockStatus || '').toLowerCase() === 'in-use';
          if (hasTrailer || occupiedByStatus) next.add(dock.dockUuid);
        });
      });
      setOccupiedDockIds(next);
    } catch (e) {
      console.warn('Failed to load warehouse occupancy for UI locks', e);
    }
  };

  const handleWarehouseEditOpen = () => {
    loadWarehouseOccupancy();
    setShowWarehouseEditModal(true);
  };

  const handleWarehouseEditClose = () => {
    setShowWarehouseEditModal(false);
    setEditingDock(null);
    onCancel?.();
  };

  const handleWarehouseEditSave = async () => {
    setSavingChanges(true);
    
    try {
      // Get auth token from cookie
      const authToken = getAuthToken();
      
      // Keep track of zone updates to apply to local state
      const zoneUpdates: { [key: string]: Partial<WarehouseZone> } = {};
      
      // Process each pending zone change
      for (const [zoneId, changes] of Object.entries(pendingZoneChanges)) {
        // Check if this is a new zone or an update
        if (changes.isNew) {
          // This is a new zone to create
          const input = {
            name: changes.name,
            facility_id: changes.facility_id,
            description: changes.description,
            parent_zone_uuid: changes.parent_zone_uuid,
            depth: "1",
            configuration: JSON.stringify(changes.configuration)
          };
          
          // Call the GraphQL mutation to add a new zone
          const response = await fetch(GRAPHQL_API_URL, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${authToken}`
            },
            body: JSON.stringify({
              query: ADD_WAREHOUSE_ZONE_MUTATION,
              variables: {
                input
              }
            })
          });
          
          const result = await response.json();
          
          if (result.errors) {
            console.error('Error adding warehouse zone:', result.errors);
            throw new Error(result.errors[0].message);
          }
          
          console.log('Warehouse zone added:', result.data.add_warehouse_zone);
        } else {
          // This is an existing zone to update
          const input = {
            name: changes.name,
            description: changes.description,
            configuration: JSON.stringify(changes.configuration)
          };
          
          // Call the GraphQL mutation to update an existing zone
          const response = await fetch(GRAPHQL_API_URL, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${authToken}`
            },
            body: JSON.stringify({
              query: UPDATE_WAREHOUSE_ZONE_MUTATION,
              variables: {
                uuid: zoneId,
                input
              }
            })
          });
          
          const result = await response.json();
          
          if (result.errors) {
            console.error('Error updating warehouse zone:', result.errors);
            throw new Error(result.errors[0].message);
          }
          
          console.log('Warehouse zone updated:', result.data.update_warehouse_zone);
          
          // Track successful updates for local state
          if (result.data.update_warehouse_zone) {
            zoneUpdates[zoneId] = {
              name: result.data.update_warehouse_zone.name,
              description: changes.description
            };
          }
        }
      }
      
      // Apply zone updates to local state
      if (Object.keys(zoneUpdates).length > 0) {
        setZones(prevZones => 
          prevZones.map(zone => {
            if (zoneUpdates[zone.id]) {
              return {
                ...zone,
                ...zoneUpdates[zone.id]
              };
            }
            return zone;
          })
        );
      }
      
      // Process pending dock changes (if any)
      for (const [dockId, changes] of Object.entries(pendingDockChanges)) {
        if (changes.isNew) {
          // This is a new dock to create
          const input = {
            name: changes.name,
            facility_id: changes.facility_id,
            location_id: changes.location_id,
            order_number: changes.order_number ? String(changes.order_number) : "1",
            warehouse_zone_uuid: changes.warehouse_zone_uuid,
            configuration: JSON.stringify(changes.configuration)
          };
          
          // Call the GraphQL mutation to add a new dock
          const response = await fetch(GRAPHQL_API_URL, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${authToken}`
            },
            body: JSON.stringify({
              query: ADD_DOCK_MUTATION,
              variables: {
                input
              }
            })
          });
          
          const result = await response.json();
          
          if (result.errors) {
            console.error('Error adding dock:', result.errors);
            throw new Error(result.errors[0].message);
          }
          
          console.log('Dock added:', result.data.add_dock);
        } else {
          // This is an existing dock to update
          const input = {
            name: changes.name,
            location_id: changes.location_id,
            order_number: changes.order_number ? String(changes.order_number) : undefined,
            warehouse_zone_uuid: changes.warehouse_zone_uuid,
            configuration: JSON.stringify(changes.configuration)
          };
          
          // Call the GraphQL mutation to update an existing dock
          const response = await fetch(GRAPHQL_API_URL, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${authToken}`
            },
            body: JSON.stringify({
              query: UPDATE_DOCK_MUTATION,
              variables: {
                uuid: dockId,
                input
              }
            })
          });
          
          const result = await response.json();
          
          if (result.errors) {
            console.error('Error updating dock:', result.errors);
            throw new Error(result.errors[0].message);
          }
          
          console.log('Dock updated:', result.data.update_dock);
        }
      }
      
      // Process pending facility changes
      if (Object.keys(pendingFacilityChanges).length > 0 && facility) {
        const input = {
          configuration: JSON.stringify(pendingFacilityChanges.configuration)
        };
        
        // Call the GraphQL mutation to update the facility
        const response = await fetch(GRAPHQL_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            query: UPDATE_FACILITY_MUTATION,
            variables: {
              id: facility.id,
              input
            }
          })
        });
        
        const result = await response.json();
        
        if (result.errors) {
          console.error('Error updating facility:', result.errors);
          throw new Error(result.errors[0].message);
        }
        
        console.log('Facility updated:', result.data.update_facility);
      }
      
      // Clear pending changes after successful save
      setPendingZoneChanges({});
      setPendingDockChanges({});
      setPendingFacilityChanges({});
      
      // Close the modal after saving
      handleWarehouseEditClose();
      
      // Optionally refresh data after saving
      // You could trigger a refetch here if needed
      
    } catch (error) {
      console.error('Error saving warehouse changes:', error);
      // You could show an error message to the user here
    } finally {
      setSavingChanges(false);
    }
  };

  const handleDockEditOpen = (dock: BaseDock) => {
    // Block editing entirely when occupied, to match yard spot behavior
    if (occupiedDockIds.has(dock.id)) {
      return;
    }
    setEditingDock({ ...dock });
    setOriginalDockStatus(dock.status);
    setShowDockEditModal(true);
  };

  const handleDockEditClose = () => {
    setShowDockEditModal(false);
    setEditingDock(null);
    setOriginalDockStatus(null);
  };

  const handleDockEditSave = async () => {
    if (!editingDock) return;

    // Confirm save
    const confirmed = window.confirm('Are you sure you want to save these changes?');
    if (!confirmed) return;

    try {
      setLoading(true);
      
      // Get auth token from cookie
      const authToken = getAuthToken();

      if (!authToken) {
        throw new Error('Authentication required');
      }

      // If dock is occupied (live occupancy), force status to 'available' to reflect config-locked rule
      const occupied = occupiedDockIds.has(editingDock.id);
      const normalizedDock = occupied && editingDock.status !== 'available'
        ? { ...editingDock, status: 'available' as DockStatus }
        : editingDock;

      // Update the docks array locally
      setDocks(docks.map(dock => 
        dock.id === normalizedDock.id ? normalizedDock : dock
      ));

      // Prepare the configuration
      const currentConfig = typeof normalizedDock.configuration === 'string' 
        ? JSON.parse(normalizedDock.configuration) 
        : normalizedDock.configuration || {};
      
      const updatedConfig = {
        ...currentConfig,
        status: normalizedDock.status,
        type: normalizedDock.type,
        position: normalizedDock.position,
        notes: normalizedDock.notes
      };

      // Call the UPDATE_DOCK_MUTATION
      const input = {
        name: normalizedDock.number,
        location_id: normalizedDock.locationId || facility?.warehouse_id,
        order_number: normalizedDock.order_number ? String(normalizedDock.order_number) : undefined,
        warehouse_zone_uuid: normalizedDock.zoneId,
        configuration: JSON.stringify(updatedConfig)
      };

      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: UPDATE_DOCK_MUTATION,
          variables: {
            uuid: normalizedDock.id,
            input
          }
        })
      });

      const result = await response.json();

      if (result.errors) {
        console.error('Error updating dock:', result.errors);
        throw new Error(result.errors[0].message);
      }

      console.log('Dock updated successfully:', result.data.update_dock);
      alert('Dock updated successfully!');
      handleDockEditClose();
      
    } catch (error: any) {
      console.error('Error updating dock:', error);
      alert(`Failed to update dock: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleZoneEditOpen = (zone: WarehouseZone) => {
    setEditingZone({ ...zone });
    setShowZoneEditModal(true);
  };

  const handleZoneEditClose = () => {
    setShowZoneEditModal(false);
    setEditingZone(null);
  };

  const handleZoneEditSave = async () => {
    if (editingZone) {
      try {
        // Get auth token from cookie
        const authToken = getAuthToken();

        // Prepare the input for the API
        const input = {
          name: editingZone.name,
          description: editingZone.description || '',
          configuration: JSON.stringify({
            color: editingZone.color,
            dockType: editingZone.defaultDockType
          })
        };

        // Call the GraphQL mutation to update the zone
        const response = await fetch(GRAPHQL_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            query: UPDATE_WAREHOUSE_ZONE_MUTATION,
            variables: {
              uuid: editingZone.id,
              input
            }
          })
        });

        const result = await response.json();

        if (result.errors) {
          console.error('Error updating warehouse zone:', result.errors);
          throw new Error(result.errors[0].message);
        }

        console.log('Zone updated successfully:', result.data.update_warehouse_zone);

        // Update local state with the successful response
        if (result.data.update_warehouse_zone) {
          setZones(prevZones => 
            prevZones.map(zone => 
              zone.id === editingZone.id ? {
                ...zone,
                name: result.data.update_warehouse_zone.name,
                description: result.data.update_warehouse_zone.description,
                color: editingZone.color,
                defaultDockType: editingZone.defaultDockType
              } : zone
            )
          );
        }

        handleZoneEditClose();
      } catch (error) {
        console.error('Error saving zone changes:', error);
        // You could show an error message to the user here
      }
    }
  };

  // Add zone management function
  const handleAddZone = () => {
    // Check if we're editing the warehouse
    if (!showWarehouseEditModal) {
      handleWarehouseEditOpen();
      return;
    }

    // Generate a random color
    const randomColor = `#${Math.floor(Math.random()*16777215).toString(16)}`;
    
    // For UI display, create a temporary zone with client-side ID
    const newZone: WarehouseZone = {
      id: `temp-zone-${Date.now()}`,
      name: `Zone ${zones.length + 1}`,
      color: randomColor,
      description: `Warehouse zone ${zones.length + 1}`,
      defaultDockType: DOCK_TYPES[0].value // Use default dock type from configuration or fallback
    };
    
    setZones([...zones, newZone]);
    
    // If we have warehouse data, prepare to create a new zone in the database
    if (warehouse) {
      // Create a new zone entry to be sent to the API on save
      const newZoneInput = {
        name: newZone.name,
        facility_id: facility?.id || warehouse.facilityId, // Use facility ID from URL first, fallback to warehouse
        description: newZone.description,
        parent_zone_uuid: null, // Warehouse zones typically don't have parents
        depth: "1", // Warehouse zones are typically depth 1
        configuration: {
          color: randomColor,
          dockType: newZone.defaultDockType,
          position: {
            x: 100,
            y: 100
          },
          size: {
            width: 200,
            height: 150
          }
        }
      };
      
      // Store this as a new zone to be created on save
      setPendingZoneChanges(prev => ({
        ...prev,
        [newZone.id]: {
          ...newZoneInput,
          isNew: true // Flag that this is a new zone, not an update
        }
      }));
    }
  };

  // Add zone name change handler
  const handleZoneNameChange = (id: string, newName: string) => {
    // Update the UI state
    setZones(zones.map(zone => 
      zone.id === id ? { ...zone, name: newName } : zone
    ));
    
    // Track the change for later submission
    if (originalZones.includes(id)) {
      // Existing zone - update it
      setPendingZoneChanges(prev => ({
        ...prev,
        [id]: {
          ...prev[id],
          name: newName
        }
      }));
    } else {
      // New zone - update its configuration
      setPendingZoneChanges(prev => ({
        ...prev,
        [id]: {
          ...prev[id],
          name: newName
        }
      }));
    }
  };

  // Add zone color change handler
  const handleZoneColorChange = (id: string, newColor: string) => {
    // Update the UI state
    setZones(zones.map(zone => 
      zone.id === id ? { ...zone, color: newColor } : zone
    ));
    
    // Track the color change for both existing and new zones
    if (originalZones.includes(id)) {
      // Existing zone - track in configuration
      setPendingZoneChanges(prev => ({
        ...prev,
        [id]: {
          ...prev[id],
          configuration: {
            ...(prev[id]?.configuration || {}),
            color: newColor
          }
        }
      }));
    } else {
      // New zone - update its configuration
      setPendingZoneChanges(prev => ({
        ...prev,
        [id]: {
          ...prev[id],
          configuration: {
            ...(prev[id]?.configuration || {}),
            color: newColor
          }
        }
      }));
    }
  };

  // Add zone description change handler
  const handleZoneDescriptionChange = (id: string, newDescription: string) => {
    // Update the UI state
    setZones(zones.map(zone => 
      zone.id === id ? { ...zone, description: newDescription } : zone
    ));
    
    // Track the change for later submission
    if (originalZones.includes(id)) {
      // Existing zone - update it
      setPendingZoneChanges(prev => ({
        ...prev,
        [id]: {
          ...prev[id],
          description: newDescription
        }
      }));
    } else {
      // New zone - update its configuration
      setPendingZoneChanges(prev => ({
        ...prev,
        [id]: {
          ...prev[id],
          description: newDescription
        }
      }));
    }
  };

  // Add function to get pending changes count
  const getPendingChangesCount = () => {
    return Object.keys(pendingZoneChanges).length + Object.keys(pendingDockChanges).length + Object.keys(pendingFacilityChanges).length;
  };

  // Add dock management function
  const handleAddDock = () => {
    // Check if we're editing the warehouse
    if (!showWarehouseEditModal) {
      handleWarehouseEditOpen();
      return;
    }

    // Must have at least one zone to assign the dock to
    if (zones.length === 0) {
      console.warn('Cannot add dock: no zones available');
      return;
    }

    // Use the first zone as default
    const defaultZone = zones[0];
    const defaultZoneId = defaultZone.id;
    
    // For UI display, create a temporary dock with client-side ID
    const newDock: BaseDock = {
      id: `temp-dock-${Date.now()}`,
      number: `D${docks.length + 1}`,
      status: 'available',
      type: defaultZone.defaultDockType, // Use zone's default dock type
      zoneId: defaultZoneId,
      order_number: docks.length + 1,
      position: 'top' // Default position that will be reassigned by layout
    };
    
    // Add the new dock and immediately assign positions based on current layout
    const updatedDocks = [...docks, newDock];
    const docksWithPositions = assignDockPositionsByLayout(updatedDocks, layout);
    setDocks(docksWithPositions);
    
    // If we have warehouse data, prepare to create a new dock in the database
    if (warehouse) {
      // Create a new dock entry to be sent to the API on save
      const newDockInput = {
        name: newDock.number,
        facility_id: facility?.id || warehouse.facilityId, // Use facility ID from URL first, fallback to warehouse
        location_id: null, // Can be set later if needed
        order_number: newDock.order_number,
        warehouse_zone_uuid: defaultZoneId,
        configuration: {
          status: newDock.status,
          type: newDock.type,
          position: newDock.position,
          coordinates: {
            x: 0,
            y: 0
          }
        }
      };
      
      // Store this as a new dock to be created on save
      setPendingDockChanges(prev => ({
        ...prev,
        [newDock.id]: {
          ...newDockInput,
          isNew: true // Flag that this is a new dock, not an update
        }
      }));
    }
  };

  // Add dock property change handlers
  const handleDockNameChange = (id: string, newName: string) => {
    // Update the UI state
    setDocks(docks.map(dock => 
      dock.id === id ? { ...dock, number: newName } : dock
    ));
    
    // Track the change for later submission - handle both new docks and existing docks
    setPendingDockChanges(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        name: newName
      }
    }));
  };

  const handleDockStatusChange = (id: string, newStatus: DockStatus) => {
    // Update the UI state
    setDocks(docks.map(dock => 
      dock.id === id ? { ...dock, status: newStatus } : dock
    ));
    
    // Track the change for later submission - handle both new docks and existing docks
    setPendingDockChanges(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        configuration: {
          ...(prev[id]?.configuration || {}),
          status: newStatus
        }
      }
    }));
  };

  const handleDockTypeChange = (id: string, newType: DockType) => {
    // Update the UI state
    setDocks(docks.map(dock => 
      dock.id === id ? { ...dock, type: newType } : dock
    ));
    
    // Track the change for later submission - handle both new docks and existing docks
    setPendingDockChanges(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        configuration: {
          ...(prev[id]?.configuration || {}),
          type: newType
        }
      }
    }));
  };

  const handleDockZoneChange = (id: string, newZoneId: string) => {
    // Update the UI state
    setDocks(docks.map(dock => 
      dock.id === id ? { ...dock, zoneId: newZoneId } : dock
    ));
    
    // Track the change for later submission - handle both new docks and existing docks
    setPendingDockChanges(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        warehouse_zone_uuid: newZoneId
      }
    }));
  };

  // Helper function to get dock color by status
  const getDockColor = (status: DockStatus): string => {
    switch (status) {
      case 'available': return '#10b981'; // green
      case 'in-use': return '#ef4444'; // red
      case 'unavailable': return '#6b7280'; // gray
      default: return '#6b7280';
    }
  };

  // Helper function to get dock border color by type
  const getDockBorderColor = (type: DockType): string => {
    const dockType = DOCK_TYPES.find(dt => dt.value === type);
    return dockType?.color || '#6b7280';
  };

  // Filter docks based on status filter and sort by order_number
  const filteredDocks = (statusFilter === 'all' 
    ? docks 
    : docks.filter(dock => dock.status === statusFilter))
    .sort((a, b) => (a.order_number || 0) - (b.order_number || 0));

  // Add drag and drop handlers for docks
  const reorderDocksWithinZone = (docksToReorder: BaseDock[], sourceIndex: number, destinationIndex: number): BaseDock[] => {
    const reorderedDocks = [...docksToReorder];
    const [removed] = reorderedDocks.splice(sourceIndex, 1);
    reorderedDocks.splice(destinationIndex, 0, removed);
    
    // Update order_number for all docks in the zone to maintain relative positioning
    return reorderedDocks.map((dock, index) => ({
      ...dock,
      order_number: index + 1
    }));
  };

  const handleDockDragStart = (e: React.DragEvent, dock: BaseDock) => {
    setDraggedDock(dock);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDockDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDockDragEnd = () => {
    setDraggedDock(null);
    setDragOverZone(null);
  };

  const handleDockDrop = (e: React.DragEvent, targetDock: BaseDock) => {
    e.preventDefault();
    
    if (!draggedDock || draggedDock.id === targetDock.id) {
      return;
    }

    // Only allow reordering within the same zone
    if (draggedDock.zoneId !== targetDock.zoneId) {
      console.log('Cannot reorder docks across different zones');
      return;
    }

    // Get all docks in the same zone, sorted by order_number
    const zoneId = draggedDock.zoneId;
    const docksInZone = docks
      .filter(dock => dock.zoneId === zoneId)
      .sort((a, b) => (a.order_number || 0) - (b.order_number || 0));
    
    const sourceIndex = docksInZone.findIndex(dock => dock.id === draggedDock.id);
    const destinationIndex = docksInZone.findIndex(dock => dock.id === targetDock.id);
    
    if (sourceIndex === -1 || destinationIndex === -1) {
      console.error('Could not find dock indices');
      return;
    }

    // Reorder docks within the zone
    const reorderedZoneDocks = reorderDocksWithinZone(docksInZone, sourceIndex, destinationIndex);
    
    // Update the main docks array with reordered zone docks
    const updatedDocks = docks.map(dock => {
      const reorderedDock = reorderedZoneDocks.find(zoneDock => zoneDock.id === dock.id);
      return reorderedDock || dock;
    });
    
    setDocks(updatedDocks);
    
    // Track the order number changes for API updates
    reorderedZoneDocks.forEach(dock => {
      setPendingDockChanges(prev => ({
        ...prev,
        [dock.id]: {
          ...prev[dock.id],
          order_number: dock.order_number
        }
      }));
    });
  };

  // Helper function to get docks by position with fallback logic
  const getDocksByPosition = (position: DockPosition): BaseDock[] => {
    const docksWithPosition = filteredDocks.filter(dock => dock.position === position);
    
    console.log(`🔍 getDocksByPosition(${position}) - found ${docksWithPosition.length} docks with assigned position`);
    
    // If no docks have positions assigned yet (fallback for existing data), distribute evenly
    if (docksWithPosition.length === 0 && filteredDocks.some(dock => !dock.position)) {
      const docksWithoutPosition = filteredDocks.filter(dock => !dock.position);
      
      console.log(`🔍 No docks with position ${position}, distributing ${docksWithoutPosition.length} unassigned docks for layout: ${layout}`);
      
      if (layout === 'single-sided') {
        return position === 'top' ? docksWithoutPosition : [];
      } else if (layout === 'double-sided') {
        const half = Math.ceil(docksWithoutPosition.length / 2);
        if (position === 'top') return docksWithoutPosition.slice(0, half);
        if (position === 'bottom') return docksWithoutPosition.slice(half);
        return [];
      } else if (layout === 'u-shaped') {
        const third = Math.ceil(docksWithoutPosition.length / 3);
        if (position === 'top') return docksWithoutPosition.slice(0, third);
        if (position === 'left') return docksWithoutPosition.slice(third, third * 2);
        if (position === 'right') return docksWithoutPosition.slice(third * 2);
        return [];
      } else if (layout === 'square') {
        const quarter = Math.ceil(docksWithoutPosition.length / 4);
        let result: BaseDock[] = [];
        if (position === 'top') {
          result = docksWithoutPosition.slice(0, quarter);
        } else if (position === 'right') {
          result = docksWithoutPosition.slice(quarter, quarter * 2);
        } else if (position === 'bottom') {
          result = docksWithoutPosition.slice(quarter * 2, quarter * 3);
        } else if (position === 'left') {
          result = docksWithoutPosition.slice(quarter * 3);
        }
        return result;
      }
      
      return []; // Default return for unhandled layout types
    }
    
    return docksWithPosition;
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center mt-4 w-full">
        <CircularProgress />
        <p className="mt-2">Loading...</p>
      </div>
    );
  }

  // If no warehouse data is available, show a message
  if (!warehouse) {
    return (
      <div className="w-full">
        <div className="shadow bg-[#FBFBFB] p-3">
          <p className="text-gray-500">No warehouse data available for this facility</p>
        </div>
      </div>
    );
  }

  const statusOptions = [
    { value: 'all', label: 'All' },
    { value: 'available', label: 'Available' },
    { value: 'in-use', label: 'In Use' },
    { value: 'unavailable', label: 'Unavailable' }
  ];

  const handleConfigModalTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setConfigModalTab(newValue);
  };

  const saveConfiguration = async () => {
    try {
      // Track the configuration change for batch saving
      setPendingFacilityChanges(prev => ({
        ...prev,
        configuration: {
          ...(prev.configuration || {}),
          dock: {
            colorConfig,
            thresholds: {
              mdt: {
                warning: colorConfig.mdt.warningThreshold,
                critical: colorConfig.mdt.criticalThreshold
              },
              detention: {
                warning: colorConfig.detention.warningThreshold,
                critical: colorConfig.detention.criticalThreshold
              },
              dockTimer: {
                warning: colorConfig.dockTimer.warningThreshold,
                critical: colorConfig.dockTimer.criticalThreshold
              }
            },
            lastUpdated: new Date().toISOString()
          }
        }
      }));
      
      console.log('Timer color configuration staged for saving:', colorConfig);
      setShowConfigModal(false);
    } catch (error) {
      console.error('Failed to save configuration:', error);
    }
  };

  const getTimeStatus = (timeString: string, timerType: 'mdt' | 'detention' | 'dockTimer'): 'red' | 'green' | 'orange' => {
    if (!timeString) return 'green';
    
    // Extract minutes from timestring
    const matches = timeString.match(/\d+/g);
    if (matches && matches.length >= 2) {
      const hours = parseInt(matches[0]);
      const mins = parseInt(matches[1]);
      const totalMinutes = hours * 60 + mins;

      // if all 0s return green
      if (totalMinutes === 0) return 'green';

      if (timerType === 'mdt' || timerType === 'detention') {
        // if first character is - then make it red
        if (timeString.startsWith('-')) {
          return 'red';
        }
        // For MDT and detention, LARGER minutes are good (more time remaining)
        // So warning is when time gets low, critical is when time gets very low
        if (totalMinutes < colorConfig[timerType].criticalThreshold) return 'red';
        if (totalMinutes < colorConfig[timerType].warningThreshold) return 'orange';
      } else if (timerType === 'dockTimer') {
        // For dock timer, SMALLER minutes are good (less time spent)
        // So warning is when time gets high, critical when time gets very high
        if (totalMinutes > colorConfig[timerType].criticalThreshold) return 'red';
        if (totalMinutes > colorConfig[timerType].warningThreshold) return 'orange';
      }
      
      return 'green';
    }
    
    return 'green';
  };

  const getTimerBackgroundColor = (type: 'mdt' | 'detention' | 'dockTimer', status: 'red' | 'green' | 'orange') => {
    if (status === 'red') return colorConfig[type].critical;
    if (status === 'orange') return colorConfig[type].warning;
    return colorConfig[type].good;
  };

  const getCardBackgroundColor = (dock: BaseDock) => {
    // If dock has no status, it's empty - show as green
    if (!dock.status) {
      return colorConfig.card.default;
    }

    // Count warning and critical indicators
    const mdtStatus = getTimeStatus(dock.mdt || '00:00:00', 'mdt');
    const detentionStatus = getTimeStatus(dock.detention || '00:00:00', 'detention');
    const dockTimerStatus = getTimeStatus(dock.dock_timer || '00:00:00', 'dockTimer');
    
    const warningCount = [mdtStatus, detentionStatus, dockTimerStatus].filter(status => status === 'orange').length;
    const criticalCount = [mdtStatus, detentionStatus, dockTimerStatus].filter(status => status === 'red').length;
    
    // Determine card color based on counts
    if (criticalCount > 0) {
      if (warningCount > 0) {
        // Mixed warning and critical
        return colorConfig.card.mixed;
      } else {
        // Only critical indicators
        if (criticalCount === 1) return colorConfig.card.oneCritical;
        if (criticalCount === 2) return colorConfig.card.twoCriticals;
        if (criticalCount === 3) return colorConfig.card.threeCriticals;
      }
    } else if (warningCount > 0) {
      // Only warning indicators
      if (warningCount === 1) return colorConfig.card.oneWarning;
      if (warningCount === 2) return colorConfig.card.twoWarnings;
      if (warningCount === 3) return colorConfig.card.threeWarnings;
    }
    
    // Default case (all good)
    return colorConfig.card.default;
  };

  return (
    <div className="w-full">
      <div className="flex mb-4 items-center">
        <div className="mr-4">
          {userIsAdmin && (<button
            className="bg-sky-600 hover:bg-sky-500 text-white px-3 py-1 rounded flex items-center"
            onClick={handleWarehouseEditOpen}
          >
            <Edit fontSize="small" className="mr-1" />
            Edit Warehouse Layout
          </button>
          )}
        </div>
      </div>

      {/* Main warehouse layout container */}
      <div className="border border-gray-300 bg-gray-100 relative mb-8">
        {/* Use CSS grid for overall layout */}
        <div className={`${layout === 'single-sided' ? 'grid grid-rows-1' : 'flex flex-col'} min-h-[400px]`}>
          
          {layout === 'single-sided' ? (
            // Single-sided layout (warehouse with doors on one side only)
            <>
              <div className="bg-blue-50 border-2 border-gray-600 p-4 m-4 rounded flex flex-col">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="font-bold text-center">Warehouse - Single-Sided</h3>
                  <div className="flex text-xs gap-2">
                    {zones.map(zone => (
                      <div key={zone.id} className="flex items-center">
                        <div className="w-3 h-3 mr-1" style={{ backgroundColor: zone.color }}></div>
                        <span>{zone.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Dock doors layout */}
                <div className="flex-1 flex flex-col">
                  {/* Top docks */}
                  <div className="flex flex-wrap justify-center gap-1 mb-4">
                    {getDocksByPosition('top').map(dock => {
                      const zone = zones.find(z => z.id === dock.zoneId);
                      return (
                        <Tooltip key={dock.id} title={`${dock.number} - ${dock.status} - ${zone?.name || 'No zone'}`}>
                          <div
                            className="w-12 h-8 border-2 cursor-pointer hover:opacity-80 flex items-center justify-center relative"
                            style={{
                              backgroundColor: getDockColor(dock.status),
                              borderColor: getDockBorderColor(dock.type)
                            }}
                            onClick={() => handleDockClick(dock)}
                          >
                            <span className="text-xs font-bold text-white">{dock.number}</span>
                            {dockHasLock(dock.id) && (
                              <Lock 
                                className="absolute top-0 right-0 text-orange-500 bg-white rounded-full p-0.5" 
                                style={{ fontSize: '12px' }} 
                              />
                            )}
                          </div>
                        </Tooltip>
                      );
                    })}
                  </div>
                  
                  {/* Warehouse floor area */}
                  <div className="flex-1 bg-gray-200 border border-gray-400 rounded p-4 min-h-[200px] flex items-center justify-center">
                    <span className="text-gray-600 font-semibold">Warehouse Floor</span>
                  </div>
                </div>
              </div>
            </>
          ) : layout === 'double-sided' ? (
            // Double-sided layout (warehouse with doors on top AND bottom)
            <>
              <div className="bg-blue-50 border-2 border-gray-600 p-4 m-4 rounded flex flex-col">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="font-bold text-center">Warehouse - Double-Sided</h3>
                  <div className="flex text-xs gap-2">
                    {zones.map(zone => (
                      <div key={zone.id} className="flex items-center">
                        <div className="w-3 h-3 mr-1" style={{ backgroundColor: zone.color }}></div>
                        <span>{zone.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="flex-1 flex flex-col">
                  {/* Top docks */}
                  <div className="flex flex-wrap justify-center gap-1 mb-4">
                    {getDocksByPosition('top').map(dock => {
                      const zone = zones.find(z => z.id === dock.zoneId);
                      return (
                        <Tooltip key={dock.id} title={`${dock.number} - ${dock.status} - ${zone?.name || 'No zone'}`}>
                          <div
                            className="w-12 h-8 border-2 cursor-pointer hover:opacity-80 flex items-center justify-center relative"
                            style={{
                              backgroundColor: getDockColor(dock.status),
                              borderColor: getDockBorderColor(dock.type)
                            }}
                            onClick={() => handleDockClick(dock)}
                          >
                            <span className="text-xs font-bold text-white">{dock.number}</span>
                            {dockHasLock(dock.id) && (
                              <Lock 
                                className="absolute top-0 right-0 text-orange-500 bg-white rounded-full p-0.5" 
                                style={{ fontSize: '12px' }} 
                              />
                            )}
                          </div>
                        </Tooltip>
                      );
                    })}
                  </div>
                  
                  {/* Warehouse floor area */}
                  <div className="flex-1 bg-gray-200 border border-gray-400 rounded p-4 min-h-[200px] flex items-center justify-center">
                    <span className="text-gray-600 font-semibold">Warehouse Floor</span>
                  </div>
                  
                  {/* Bottom docks */}
                  <div className="flex flex-wrap justify-center gap-1 mt-4">
                    {getDocksByPosition('bottom').map(dock => {
                      const zone = zones.find(z => z.id === dock.zoneId);
                      return (
                        <Tooltip key={dock.id} title={`${dock.number} - ${dock.status} - ${zone?.name || 'No zone'}`}>
                          <div
                            className="w-12 h-8 border-2 cursor-pointer hover:opacity-80 flex items-center justify-center relative"
                            style={{
                              backgroundColor: getDockColor(dock.status),
                              borderColor: getDockBorderColor(dock.type)
                            }}
                            onClick={() => handleDockClick(dock)}
                          >
                            <span className="text-xs font-bold text-white">{dock.number}</span>
                            {dockHasLock(dock.id) && (
                              <Lock 
                                className="absolute top-0 right-0 text-orange-500 bg-white rounded-full p-0.5" 
                                style={{ fontSize: '12px' }} 
                              />
                            )}
                          </div>
                        </Tooltip>
                      );
                    })}
                  </div>
                </div>
              </div>
            </>
          ) : layout === 'u-shaped' ? (
            // U-shaped layout (warehouse with doors on three sides)
            <>
              <div className="bg-blue-50 border-2 border-gray-600 p-4 m-4 rounded flex flex-col">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="font-bold text-center">Warehouse - U-Shaped</h3>
                  <div className="flex text-xs gap-2">
                    {zones.map(zone => (
                      <div key={zone.id} className="flex items-center">
                        <div className="w-3 h-3 mr-1" style={{ backgroundColor: zone.color }}></div>
                        <span>{zone.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="flex-1 flex flex-col">
                  {/* Top docks */}
                  <div className="flex flex-wrap justify-center gap-1 mb-2">
                    {filteredDocks.filter(dock => dock.position === 'top').map(dock => {
                      const zone = zones.find(z => z.id === dock.zoneId);
                      return (
                        <Tooltip key={dock.id} title={`${dock.number} - ${dock.status} - ${zone?.name || 'No zone'}`}>
                          <div
                            className="w-12 h-8 border-2 cursor-pointer hover:opacity-80 flex items-center justify-center relative"
                            style={{
                              backgroundColor: getDockColor(dock.status),
                              borderColor: getDockBorderColor(dock.type)
                            }}
                            onClick={() => handleDockClick(dock)}
                          >
                            <span className="text-xs font-bold text-white">{dock.number}</span>
                            {dockHasLock(dock.id) && (
                              <Lock 
                                className="absolute top-0 right-0 text-orange-500 bg-white rounded-full p-0.5" 
                                style={{ fontSize: '12px' }} 
                              />
                            )}
                          </div>
                        </Tooltip>
                      );
                    })}
                  </div>
                  
                  {/* Middle section with side docks and warehouse floor */}
                  <div className="flex-1 flex">
                    {/* Left docks */}
                    <div className="flex flex-col justify-center gap-1 mr-2">
                      {getDocksByPosition('left').map(dock => {
                        const zone = zones.find(z => z.id === dock.zoneId);
                        return (
                          <Tooltip key={dock.id} title={`${dock.number} - ${dock.status} - ${zone?.name || 'No zone'}`}>
                            <div
                              className="w-8 h-12 border-2 cursor-pointer hover:opacity-80 flex items-center justify-center"
                              style={{
                                backgroundColor: getDockColor(dock.status),
                                borderColor: getDockBorderColor(dock.type)
                              }}
                              onClick={() => handleDockClick(dock)}
                            >
                              <span className="text-xs font-bold text-white transform -rotate-90">{dock.number}</span>
                            </div>
                          </Tooltip>
                        );
                      })}
                    </div>
                    
                    {/* Warehouse floor area */}
                    <div className="flex-1 bg-gray-200 border border-gray-400 rounded p-4 min-h-[200px] flex items-center justify-center">
                      <span className="text-gray-600 font-semibold">Warehouse Floor</span>
                    </div>
                    
                    {/* Right docks */}
                    <div className="flex flex-col justify-center gap-1 ml-2">
                      {getDocksByPosition('right').map(dock => {
                        const zone = zones.find(z => z.id === dock.zoneId);
                        return (
                          <Tooltip key={dock.id} title={`${dock.number} - ${dock.status} - ${zone?.name || 'No zone'}`}>
                            <div
                              className="w-8 h-12 border-2 cursor-pointer hover:opacity-80 flex items-center justify-center"
                              style={{
                                backgroundColor: getDockColor(dock.status),
                                borderColor: getDockBorderColor(dock.type)
                              }}
                              onClick={() => handleDockClick(dock)}
                            >
                              <span className="text-xs font-bold text-white transform rotate-90">{dock.number}</span>
                            </div>
                          </Tooltip>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : layout === 'square' ? (
            // Square layout (warehouse with doors on all four sides)
            <>
              <div className="bg-blue-50 border-2 border-gray-600 p-4 m-4 rounded flex flex-col">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="font-bold text-center">Warehouse - Square</h3>
                  <div className="flex text-xs gap-2">
                    {zones.map(zone => (
                      <div key={zone.id} className="flex items-center">
                        <div className="w-3 h-3 mr-1" style={{ backgroundColor: zone.color }}></div>
                        <span>{zone.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="flex-1 flex flex-col">
                  {/* Top docks */}
                  <div className="flex flex-wrap justify-center gap-1 mb-2">
                    {getDocksByPosition('top').map(dock => {
                      const zone = zones.find(z => z.id === dock.zoneId);
                      return (
                        <Tooltip key={dock.id} title={`${dock.number} - ${dock.status} - ${zone?.name || 'No zone'} - Top`}>
                          <div
                            className="w-12 h-8 border-2 cursor-pointer hover:opacity-80 flex items-center justify-center relative"
                            style={{
                              backgroundColor: getDockColor(dock.status),
                              borderColor: getDockBorderColor(dock.type)
                            }}
                            onClick={() => handleDockClick(dock)}
                          >
                            <span className="text-xs font-bold text-white">{dock.number}</span>
                            {dockHasLock(dock.id) && (
                              <Lock 
                                className="absolute top-0 right-0 text-orange-500 bg-white rounded-full p-0.5" 
                                style={{ fontSize: '12px' }} 
                              />
                            )}
                          </div>
                        </Tooltip>
                      );
                    })}
                  </div>
                  
                  {/* Middle section with side docks and warehouse floor */}
                  <div className="flex-1 flex">
                    {/* Left docks */}
                    <div className="flex flex-col justify-center gap-1 mr-2">
                      {getDocksByPosition('left').map(dock => {
                        const zone = zones.find(z => z.id === dock.zoneId);
                        return (
                          <Tooltip key={dock.id} title={`${dock.number} - ${dock.status} - ${zone?.name || 'No zone'} - Left`}>
                            <div
                              className="w-8 h-12 border-2 cursor-pointer hover:opacity-80 flex items-center justify-center"
                              style={{
                                backgroundColor: getDockColor(dock.status),
                                borderColor: getDockBorderColor(dock.type)
                              }}
                              onClick={() => handleDockClick(dock)}
                            >
                              <span className="text-xs font-bold text-white transform -rotate-90">{dock.number}</span>
                            </div>
                          </Tooltip>
                        );
                      })}
                    </div>
                    
                    {/* Warehouse floor area */}
                    <div className="flex-1 bg-gray-200 border border-gray-400 rounded p-4 min-h-[200px] flex items-center justify-center">
                      <span className="text-gray-600 font-semibold">Warehouse Floor</span>
                    </div>
                    
                    {/* Right docks */}
                    <div className="flex flex-col justify-center gap-1 ml-2">
                      {getDocksByPosition('right').map(dock => {
                        const zone = zones.find(z => z.id === dock.zoneId);
                        return (
                          <Tooltip key={dock.id} title={`${dock.number} - ${dock.status} - ${zone?.name || 'No zone'} - Right`}>
                            <div
                              className="w-8 h-12 border-2 cursor-pointer hover:opacity-80 flex items-center justify-center"
                              style={{
                                backgroundColor: getDockColor(dock.status),
                                borderColor: getDockBorderColor(dock.type)
                              }}
                              onClick={() => handleDockClick(dock)}
                            >
                              <span className="text-xs font-bold text-white transform rotate-90">{dock.number}</span>
                            </div>
                          </Tooltip>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* Bottom docks */}
                  <div className="flex flex-wrap justify-center gap-1 mt-2">
                    {getDocksByPosition('bottom').map(dock => {
                      const zone = zones.find(z => z.id === dock.zoneId);
                      return (
                        <Tooltip key={dock.id} title={`${dock.number} - ${dock.status} - ${zone?.name || 'No zone'} - Bottom`}>
                          <div
                            className="w-12 h-8 border-2 cursor-pointer hover:opacity-80 flex items-center justify-center relative"
                            style={{
                              backgroundColor: getDockColor(dock.status),
                              borderColor: getDockBorderColor(dock.type)
                            }}
                            onClick={() => handleDockClick(dock)}
                          >
                            <span className="text-xs font-bold text-white">{dock.number}</span>
                            {dockHasLock(dock.id) && (
                              <Lock 
                                className="absolute top-0 right-0 text-orange-500 bg-white rounded-full p-0.5" 
                                style={{ fontSize: '12px' }} 
                              />
                            )}
                          </div>
                        </Tooltip>
                      );
                    })}
                  </div>
                </div>
              </div>
            </>
          ) : (
            // Default double-sided layout fallback
            <>
              <div className="bg-blue-50 border-2 border-gray-600 p-4 m-4 rounded flex flex-col">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="font-bold text-center">Warehouse - Double-Sided</h3>
                  <div className="flex text-xs gap-2">
                    {zones.map(zone => (
                      <div key={zone.id} className="flex items-center">
                        <div className="w-3 h-3 mr-1" style={{ backgroundColor: zone.color }}></div>
                        <span>{zone.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="flex-1 flex flex-col">
                  {/* Top docks */}
                  <div className="flex flex-wrap justify-center gap-1 mb-4">
                    {getDocksByPosition('top').map(dock => {
                      const zone = zones.find(z => z.id === dock.zoneId);
                      return (
                        <Tooltip key={dock.id} title={`${dock.number} - ${dock.status} - ${zone?.name || 'No zone'}`}>
                          <div
                            className="w-12 h-8 border-2 cursor-pointer hover:opacity-80 flex items-center justify-center relative"
                            style={{
                              backgroundColor: getDockColor(dock.status),
                              borderColor: getDockBorderColor(dock.type)
                            }}
                            onClick={() => handleDockClick(dock)}
                          >
                            <span className="text-xs font-bold text-white">{dock.number}</span>
                            {dockHasLock(dock.id) && (
                              <Lock 
                                className="absolute top-0 right-0 text-orange-500 bg-white rounded-full p-0.5" 
                                style={{ fontSize: '12px' }} 
                              />
                            )}
                          </div>
                        </Tooltip>
                      );
                    })}
                  </div>
                  
                  {/* Warehouse floor area */}
                  <div className="flex-1 bg-gray-200 border border-gray-400 rounded p-4 min-h-[200px] flex items-center justify-center">
                    <span className="text-gray-600 font-semibold">Warehouse Floor</span>
                  </div>
                  
                  {/* Bottom docks */}
                  <div className="flex flex-wrap justify-center gap-1 mt-4">
                    {getDocksByPosition('bottom').map(dock => {
                      const zone = zones.find(z => z.id === dock.zoneId);
                      return (
                        <Tooltip key={dock.id} title={`${dock.number} - ${dock.status} - ${zone?.name || 'No zone'}`}>
                          <div
                            className="w-12 h-8 border-2 cursor-pointer hover:opacity-80 flex items-center justify-center relative"
                            style={{
                              backgroundColor: getDockColor(dock.status),
                              borderColor: getDockBorderColor(dock.type)
                            }}
                            onClick={() => handleDockClick(dock)}
                          >
                            <span className="text-xs font-bold text-white">{dock.number}</span>
                            {dockHasLock(dock.id) && (
                              <Lock 
                                className="absolute top-0 right-0 text-orange-500 bg-white rounded-full p-0.5" 
                                style={{ fontSize: '12px' }} 
                              />
                            )}
                          </div>
                        </Tooltip>
                      );
                    })}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
        
        
        {/* Legend */}
        <div className="absolute bottom-4 right-4 bg-white p-2 border border-gray-300 rounded shadow-sm">
          <h4 className="font-bold text-sm mb-1">Legend:</h4>
          <div className="flex flex-col gap-1">
            <div className="text-xs font-semibold mb-1">Status:</div>
            <div className="flex items-center">
              <div className="w-4 h-4 bg-green-500 mr-2"></div>
              <span className="text-xs">In-Service</span>
            </div>
            <div className="flex items-center">
              <div className="w-4 h-4 bg-gray-500 mr-2"></div>
              <span className="text-xs">Unavailable</span>
            </div>
            <div className="flex items-center">
              <div className="w-4 h-4 bg-amber-400 mr-2"></div>
              <span className="text-xs">Maintenance</span>
            </div>
            <div className="text-xs font-semibold mt-2 mb-1">Dock Types:</div>
            {DOCK_TYPES.map(dockType => (
              <div key={dockType.value} className="flex items-center">
                <div className="w-4 h-4 border-2 mr-2" style={{ borderColor: dockType.color }}></div>
                <span className="text-xs">{dockType.label}</span>
              </div>
            ))}
           
          </div>
        </div>
      </div>
      
      {/* Statistics */}
      <div className="flex mt-8 gap-4">
        <div className="w-1/3 bg-white p-3 shadow">
          <h3 className="font-bold">Dock Doors</h3>
          <div className="mb-4">
            <h3 className="font-medium mb-2">Dock Door Summary</h3>
            <div className="grid grid-cols-2 gap-4 p-3 bg-gray-50 rounded">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Total Dock Doors</label>
                <div className="text-lg font-semibold text-gray-900">{docks.length}</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">By Type</label>
                <div className="text-sm space-y-1">
                  {DOCK_TYPES.map(dockType => {
                    const count = docks.filter(dock => dock.type === dockType.value).length;
                    if (count === 0) return null;
                    return (
                      <div key={dockType.value} className="flex items-center">
                        <div className="w-3 h-3 rounded mr-2" style={{ backgroundColor: dockType.color }}></div>
                        <span>{dockType.label}: {count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="w-1/3 bg-white p-3 shadow">
          <h3 className="font-bold">Dock Types</h3>
          {DOCK_TYPES.map(dockType => {
            const count = docks.filter(dock => dock.type === dockType.value).length;
            if (count === 0) return null;
            return (
              <div key={dockType.value} className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-3 h-3 rounded mr-2" style={{ backgroundColor: dockType.color }}></div>
                  <span>{dockType.label}</span>
                </div>
                <span className="font-medium">{count}</span>
              </div>
            );
          })}
          {docks.length === 0 && <p className="text-gray-500 text-sm">No docks configured</p>}
        </div>
        <div className="w-1/3 bg-white p-3 shadow">
          <h3 className="font-bold">By Zone</h3>
          <div className="max-h-36 overflow-y-auto">
            {zones.map(zone => {
              const zoneDocks = docks.filter(dock => dock.zoneId === zone.id);
              const availableCount = zoneDocks.filter(dock => dock.status === 'available').length;
              const inUseCount = zoneDocks.filter(dock => dock.status === 'in-use').length;
              
              return (
                <div key={zone.id} className="mb-1">
                  <p className="text-sm font-medium" style={{ color: zone.color }}>{zone.name}: {zoneDocks.length}</p>
                  <p className="text-xs">In-Service: {availableCount} | In Use: {inUseCount}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

     

      {/* Warehouse Edit Modal */}
      <Modal
        open={showWarehouseEditModal}
        onClose={handleWarehouseEditClose}
        className="flex justify-center items-center"
      >
        <div className="w-[1200px] bg-white border-2 border-gray-300 shadow-2xl rounded-xl p-10 max-h-[90vh] overflow-y-auto relative flex flex-col">
          <Close
            className="absolute top-6 right-6 cursor-pointer hover:text-slate-400 text-gray-600"
            onClick={handleWarehouseEditClose}
          />
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-3xl font-semibold text-gray-800">Edit Warehouse Layout</h1>
          </div>
          
          <div className="grid grid-cols-2 gap-8">
            {/* Layout Settings */}
            <div className="mb-6">
              <h2 className="font-bold mb-4 text-lg border-b pb-2">Warehouse Configuration</h2>
              
              <div className="mb-4">
                <h3 className="font-medium mb-2">Layout Type</h3>
                <ToggleButtonGroup
                  value={layout}
                  exclusive
                  onChange={handleLayoutChange}
                  size="small"
                >
                  <ToggleButton value="single-sided">
                    <ViewSidebar fontSize="small" />
                    <span className="ml-1">Single-Sided</span>
                  </ToggleButton>
                  <ToggleButton value="double-sided">
                    <ViewWeek fontSize="small" />
                    <span className="ml-1">Double-Sided</span>
                  </ToggleButton>
                  <ToggleButton value="u-shaped">
                    <ViewArray fontSize="small" />
                    <span className="ml-1">U-Shaped</span>
                  </ToggleButton>
                  <ToggleButton value="square">
                    <ViewModule fontSize="small" />
                    <span className="ml-1">Square</span>
                  </ToggleButton>
                </ToggleButtonGroup>
                {Object.keys(pendingFacilityChanges).length > 0 && (
                  <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-sm text-blue-800">
                    <strong>Facility configuration changed</strong> (will be saved when you click "Apply Changes")
                  </div>
                )}
              </div>
              
              <div className="mb-4">
                <h3 className="font-medium mb-2">Dock Door Summary</h3>
                <div className="grid grid-cols-2 gap-4 p-3 bg-gray-50 rounded">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Total Dock Doors</label>
                    <div className="text-lg font-semibold text-gray-900">{docks.length}</div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">By Type</label>
                    <div className="text-sm space-y-1">
                      {DOCK_TYPES.map(dockType => {
                        const count = docks.filter(dock => dock.type === dockType.value).length;
                        if (count === 0) return null;
                        return (
                          <div key={dockType.value} className="flex items-center">
                            <div className="w-3 h-3 rounded mr-2" style={{ backgroundColor: dockType.color }}></div>
                            <span>{dockType.label}: {count}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Zone Management */}
            <div>
              <h2 className="font-bold mb-4 text-lg border-b pb-2">Zone Configuration</h2>
              
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="font-medium">Warehouse Zones</h3>
                  <div className="flex gap-2">
                    <button
                      className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-3 py-1 rounded text-sm flex items-center"
                      onClick={() => setShowConfigModal(true)}
                    >
                      <Edit fontSize="small" className="mr-1" />
                      Configure
                    </button>
                    <button
                      className="bg-green-600 hover:bg-green-500 text-white px-3 py-1 rounded text-sm flex items-center"
                      onClick={handleAddZone}
                    >
                      <Add fontSize="small" className="mr-1" />
                      Add Zone
                    </button>
                  </div>
                </div>
                <div className="max-h-60 overflow-y-auto border rounded p-3">
                  {zones.map(zone => (
                    <div key={zone.id} className="flex items-center justify-between mb-2 p-2 border rounded hover:bg-gray-50">
                      <div className="flex items-center">
                        <div className="w-4 h-4 mr-2 rounded" style={{ backgroundColor: zone.color }}></div>
                        <div>
                          <span className="font-medium">{zone.name}</span>
                          {zone.description && <p className="text-xs text-gray-500">{zone.description}</p>}
                        </div>
                      </div>
                      <button 
                        className="text-gray-500 hover:text-gray-700"
                        onClick={() => handleZoneEditOpen(zone)}
                      >
                        <Edit fontSize="small" />
                      </button>
                    </div>
                  ))}
                  {zones.length === 0 && (
                    <p className="text-gray-500 text-sm italic text-center py-4">No zones configured yet</p>
                  )}
                </div>
                {getPendingChangesCount() > 0 && (
                  <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
                    <strong>{getPendingChangesCount()}</strong> pending change{getPendingChangesCount() !== 1 ? 's' : ''} (will be saved when you click "Apply Changes")
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Dock Management - Full Width */}
          <div className="mt-8">
            <h2 className="font-bold mb-4 text-lg border-b pb-2">Dock Configuration</h2>
            
            <div className="mb-4">
              <div className="flex justify-between items-center mb-2">
                <h3 className="font-medium">Dock Doors</h3>
                <div className="flex gap-2">
                  <button
                    className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-3 py-1 rounded text-sm flex items-center"
                    onClick={() => setShowConfigModal(true)}
                  >
                    <Edit fontSize="small" className="mr-1" />
                    Configure
                  </button>
                  <button
                    className="bg-blue-600 hover:bg-blue-500 text-white px-3 py-1 rounded text-sm flex items-center"
                    onClick={handleAddDock}
                  >
                    <Add fontSize="small" className="mr-1" />
                    Add Dock
                  </button>
                </div>
              </div>
              <div className="max-h-[400px] overflow-y-auto border rounded p-4">
                {zones.map(zone => {
                  // Get docks for this zone, sorted by order_number
                  const zoneDocksArray = docks
                    .filter(dock => dock.zoneId === zone.id)
                    .sort((a, b) => (a.order_number || 0) - (b.order_number || 0));
                  
                  if (zoneDocksArray.length === 0) {
                    return null;
                  }
                  
                  return (
                    <div key={zone.id} className="mb-6">
                      <div className="flex items-center mb-3">
                        <div 
                          className="w-4 h-4 rounded mr-2" 
                          style={{ backgroundColor: zone.color }}
                        ></div>
                        <h4 className="font-medium text-gray-700">{zone.name}</h4>
                        <span className="ml-2 text-sm text-gray-500">({zoneDocksArray.length} dock{zoneDocksArray.length !== 1 ? 's' : ''})</span>
                      </div>
                      
                      <div className="grid grid-cols-8 gap-1 ml-6">
                        {zoneDocksArray.map(dock => {
                          const dockTypeInfo = DOCK_TYPES.find(dt => dt.value === dock.type);
                          const isDragging = draggedDock?.id === dock.id;
                          const isOccupied = occupiedDockIds.has(dock.id);
                          
                          return (
                            <div 
                              key={dock.id} 
                              draggable={!isOccupied}
                              onDragStart={(e) => { if (!isOccupied) handleDockDragStart(e, dock); }}
                              onDragOver={(e) => { if (!isOccupied) handleDockDragOver(e); }}
                              onDragEnd={handleDockDragEnd}
                              onDrop={(e) => { if (!isOccupied) handleDockDrop(e, dock); }}
                              className={twMerge(
                                "relative border rounded p-1 transition-all border-gray-200",
                                isOccupied ? "opacity-60 cursor-pointer" : "hover:shadow-sm cursor-move hover:border-gray-300",
                                isDragging && "opacity-50 scale-95",
                                draggedDock && draggedDock.zoneId === dock.zoneId && "ring-1 ring-blue-300",
                              )}
                              onClick={() => handleDockEditOpen(dock)}
                            >
                              <div className="flex items-center justify-between mb-1">
                                <DragIndicator className="w-2 h-2 text-gray-400" />
                                <Edit className={twMerge("w-2 h-2", isOccupied ? "text-gray-300" : "text-gray-400 hover:text-gray-600")} />
                              </div>
                              <div className="text-xs font-medium text-gray-800 truncate" title={dock.number}>
                                {dock.number}
                              </div>
                              <div className="text-xs text-gray-500 truncate" title={zone.name}>
                                {zone.name}
                              </div>
                              <div className="flex items-center text-xs">
                                <div 
                                  className="w-1.5 h-1.5 rounded mr-1"
                                  style={{ backgroundColor: dockTypeInfo?.color || '#6b7280' }}
                                ></div>
                                <span className="text-gray-600 text-xs truncate">{dockTypeInfo?.label || dock.type}</span>
                              </div>
                              {dock.order_number && (
                                <div className="text-xs text-gray-400">
                                  #{dock.order_number}
                                </div>
                              )}
                              {isOccupied && (
                                <div className="absolute top-1 right-1 text-[9px] font-semibold text-amber-800 bg-amber-100 border border-amber-300 rounded px-1">
                                  Occupied
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
                
                {docks.length === 0 && (
                  <p className="text-gray-500 text-sm italic text-center py-8">No dock doors configured yet</p>
                )}
                
                {zones.length > 0 && zones.every(zone => docks.filter(dock => dock.zoneId === zone.id).length === 0) && (
                  <p className="text-gray-500 text-sm italic text-center py-8">No dock doors assigned to zones yet</p>
                )}
              </div>
            </div>
          </div>

          <div className="mt-8 pt-4 border-t flex justify-end">
            <button
              className="bg-gray-300 hover:bg-gray-400 text-gray-800 p-2 rounded mr-2"
              onClick={handleWarehouseEditClose}
            >
              Cancel
            </button>
            <button
              className={twMerge(
                "px-4 py-2 rounded flex items-center",
                savingChanges ? "bg-gray-400 cursor-not-allowed" : "bg-sky-600 hover:bg-sky-500",
                "text-white"
              )}
              onClick={handleWarehouseEditSave}
              disabled={savingChanges}
            >
              {savingChanges ? (
                <>
                  <CircularProgress size={16} className="mr-2" color="inherit" />
                  Saving...
                </>
              ) : (
                <>
                  {getPendingChangesCount() > 0 ? (
                    `Apply Changes (${getPendingChangesCount()})`
                  ) : (
                    "Apply Changes"
                  )}
                </>
              )}
            </button>
          </div>
        </div>
      </Modal>

      {/* Dock Edit Modal */}
      {showDockEditModal && editingDock && (
        <Modal
          open={showDockEditModal}
          onClose={handleDockEditClose}
          className="flex justify-center items-center"
        >
          <div className="w-96 bg-white border-2 border-gray-300 shadow-xl rounded-lg p-6 relative flex flex-col">
            <Close
              className="absolute top-4 right-4 cursor-pointer hover:text-slate-400 text-gray-600"
              onClick={handleDockEditClose}
            />
            <h1 className="text-xl font-semibold text-gray-800 mb-5">Edit Dock {editingDock.number}</h1>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Dock Name/Number</label>
              <input
                type="text"
                value={editingDock.number}
                onChange={(e) => setEditingDock({ ...editingDock, number: e.target.value })}
                className="w-full border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                placeholder="Enter dock name/number"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Zone</label>
              <select
                value={editingDock.zoneId}
                onChange={(e) => setEditingDock({ ...editingDock, zoneId: e.target.value })}
                className="w-full border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-sky-500 focus:border-transparent"
              >
                {zones.map(zone => (
                  <option key={zone.id} value={zone.id}>{zone.name}</option>
                ))}
              </select>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Order Number</label>
              <input
                type="number"
                value={editingDock.order_number || ''}
                onChange={(e) => setEditingDock({ ...editingDock, order_number: parseInt(e.target.value) || undefined })}
                className="w-full border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                placeholder="Enter order number for positioning"
                min="1"
              />
              <p className="text-xs text-gray-500 mt-1">
                Controls the relative position within the zone (lower numbers appear first)
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Position</label>
              <select
                value={editingDock.position || 'top'}
                onChange={(e) => setEditingDock({ ...editingDock, position: e.target.value as DockPosition })}
                className="w-full border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-sky-500 focus:border-transparent"
              >
                <option value="top">Top</option>
                <option value="bottom">Bottom</option>
                <option value="left">Left</option>
                <option value="right">Right</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Position of the dock relative to the warehouse layout (auto-assigned when layout changes)
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
              <textarea
                value={editingDock.notes || ''}
                onChange={(e) => setEditingDock({ ...editingDock, notes: e.target.value })}
                className="w-full border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                placeholder="Enter any notes or special instructions for this dock door"
                rows={3}
              />
              <p className="text-xs text-gray-500 mt-1">
                Add any special instructions, maintenance notes, or other information about this dock door
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                value={editingDock.status}
                onChange={(e) => {
                  const newStatus = e.target.value as DockStatus;
                  setEditingDock({ ...editingDock, status: newStatus });
                }}
                className="w-full border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                disabled={occupiedDockIds.has(editingDock.id)}
              >
                <option value="available">In-Service</option>
                <option value="unavailable">Unavailable</option>
              </select>
              {occupiedDockIds.has(editingDock.id) && (
                <p className="text-xs text-amber-700 mt-1">Occupied - only In-Service can be set while a trailer is present</p>
              )}
            </div>

            <div className="flex justify-end gap-2">
              <button
                className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded"
                onClick={handleDockEditClose}
              >
                Cancel
              </button>
              <button
                className="bg-sky-600 hover:bg-sky-500 text-white px-4 py-2 rounded"
                onClick={handleDockEditSave}
              >
                Save Changes
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Zone Edit Modal */}
      {showZoneEditModal && editingZone && (
        <Modal
          open={showZoneEditModal}
          onClose={handleZoneEditClose}
          className="flex justify-center items-center"
        >
          <div className="w-96 bg-white border-2 border-gray-300 shadow-xl rounded-lg p-6 relative flex flex-col">
            <Close
              className="absolute top-4 right-4 cursor-pointer hover:text-slate-400 text-gray-600"
              onClick={handleZoneEditClose}
            />
            <h1 className="text-xl font-semibold text-gray-800 mb-5">Edit Warehouse Zone</h1>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Zone Name</label>
              <input
                type="text"
                value={editingZone.name}
                onChange={(e) => setEditingZone({ ...editingZone, name: e.target.value })}
                className="w-full border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                placeholder="Enter zone name"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Zone Description</label>
              <textarea
                value={editingZone.description || ''}
                onChange={(e) => setEditingZone({ ...editingZone, description: e.target.value })}
                className="w-full border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                placeholder="Enter zone description"
                rows={3}
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Default Dock Type</label>
              <select
                value={editingZone.defaultDockType}
                onChange={(e) => setEditingZone({ ...editingZone, defaultDockType: e.target.value as DockType })}
                className="w-full border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-sky-500 focus:border-transparent"
              >
                {DOCK_TYPES.map(dockType => (
                  <option key={dockType.value} value={dockType.value}>{dockType.label}</option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                New docks added to this zone will default to this type
              </p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Zone Color</label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={editingZone.color}
                  onChange={(e) => setEditingZone({ ...editingZone, color: e.target.value })}
                  className="w-12 h-10 border border-gray-300 rounded-md cursor-pointer"
                />
                <div className="flex-1">
                  <input
                    type="text"
                    value={editingZone.color}
                    onChange={(e) => setEditingZone({ ...editingZone, color: e.target.value })}
                    className="w-full border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                    placeholder="#000000"
                  />
                </div>
              </div>
              <div className="flex items-center mt-2">
                <span 
                  className="w-4 h-4 rounded-full mr-2 border border-gray-300" 
                  style={{ backgroundColor: editingZone.color }}
                ></span>
                <span className="text-sm text-gray-600">Preview</span>
              </div>
            </div>

            <div className="mb-4 p-3 bg-gray-50 rounded-md">
              <div className="text-sm text-gray-700">
                <strong>Zone Statistics:</strong>
              </div>
              <div className="text-sm text-gray-600 mt-1">
                {docks.filter(dock => dock.zoneId === editingZone.id).length} dock doors assigned
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <button
                className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded"
                onClick={handleZoneEditClose}
              >
                Cancel
              </button>
              <button
                className="bg-sky-600 hover:bg-sky-500 text-white px-4 py-2 rounded"
                onClick={handleZoneEditSave}
              >
                Save Changes
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Add Configuration Modal */}
      {showConfigModal && (
        <Modal
          open={showConfigModal}
          onClose={() => setShowConfigModal(false)}
          className="flex justify-center items-center"
        >
          <div className="w-[700px] bg-white border p-5 max-h-[80vh] overflow-y-auto relative">
            <Close
              className="absolute top-4 right-4 cursor-pointer hover:text-slate-400"
              onClick={() => setShowConfigModal(false)}
            />
            <h1 className="text-xl font-bold mb-5">Dock Management Configuration</h1>
            
            <div className="mb-6">
              <Tabs 
                value={configModalTab} 
                onChange={handleConfigModalTabChange} 
                className="mb-4"
              >
                <Tab label="Indicator Colors" />
                <Tab label="Card Colors" />
                <Tab label="Thresholds" />
              </Tabs>
              
              {/* Content for tab 0 - Indicator Colors */}
              {configModalTab === 0 && (
                <>
                  <h2 className="font-bold text-lg mb-3">Timer Indicator Colors</h2>
                  <p className="text-sm text-gray-600 mb-4">
                    Configure the colors used for different time statuses in the dock management display.
                  </p>
                  
                  {/* MDT Colors */}
                  <div className="mb-4">
                    <h3 className="font-medium mb-2">MDT Colors</h3>
                    <div className="grid grid-cols-3 gap-4 mb-2">
                      <div>
                        <label className="block text-sm font-medium mb-1">Good Status</label>
                        <input 
                          type="color" 
                          value={colorConfig.mdt.good}
                          onChange={(e) => setColorConfig({
                            ...colorConfig,
                            mdt: {...colorConfig.mdt, good: e.target.value}
                          })}
                          className="w-full h-10 cursor-pointer"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-1">Warning Status</label>
                        <input 
                          type="color" 
                          value={colorConfig.mdt.warning}
                          onChange={(e) => setColorConfig({
                            ...colorConfig,
                            mdt: {...colorConfig.mdt, warning: e.target.value}
                          })}
                          className="w-full h-10 cursor-pointer"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-1">Critical Status</label>
                        <input 
                          type="color" 
                          value={colorConfig.mdt.critical}
                          onChange={(e) => setColorConfig({
                            ...colorConfig,
                            mdt: {...colorConfig.mdt, critical: e.target.value}
                          })}
                          className="w-full h-10 cursor-pointer"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Dock Timer Colors */}
                  <div className="mb-4">
                    <h3 className="font-medium mb-2">Dock Timer Colors</h3>
                    <div className="grid grid-cols-3 gap-4 mb-2">
                      <div>
                        <label className="block text-sm font-medium mb-1">Good Status</label>
                        <input 
                          type="color" 
                          value={colorConfig.dockTimer.good}
                          onChange={(e) => setColorConfig({
                            ...colorConfig,
                            dockTimer: {...colorConfig.dockTimer, good: e.target.value}
                          })}
                          className="w-full h-10 cursor-pointer"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-1">Warning Status</label>
                        <input 
                          type="color" 
                          value={colorConfig.dockTimer.warning}
                          onChange={(e) => setColorConfig({
                            ...colorConfig,
                            dockTimer: {...colorConfig.dockTimer, warning: e.target.value}
                          })}
                          className="w-full h-10 cursor-pointer"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-1">Critical Status</label>
                        <input 
                          type="color" 
                          value={colorConfig.dockTimer.critical}
                          onChange={(e) => setColorConfig({
                            ...colorConfig,
                            dockTimer: {...colorConfig.dockTimer, critical: e.target.value}
                          })}
                          className="w-full h-10 cursor-pointer"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Detention Timer Colors */}
                  <div className="mb-4">
                    <h3 className="font-medium mb-2">Detention Timer Colors</h3>
                    <div className="grid grid-cols-3 gap-4 mb-2">
                      <div>
                        <label className="block text-sm font-medium mb-1">Good Status</label>
                        <input 
                          type="color" 
                          value={colorConfig.detention.good}
                          onChange={(e) => setColorConfig({
                            ...colorConfig,
                            detention: {...colorConfig.detention, good: e.target.value}
                          })}
                          className="w-full h-10 cursor-pointer"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-1">Warning Status</label>
                        <input 
                          type="color" 
                          value={colorConfig.detention.warning}
                          onChange={(e) => setColorConfig({
                            ...colorConfig,
                            detention: {...colorConfig.detention, warning: e.target.value}
                          })}
                          className="w-full h-10 cursor-pointer"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-1">Critical Status</label>
                        <input 
                          type="color" 
                          value={colorConfig.detention.critical}
                          onChange={(e) => setColorConfig({
                            ...colorConfig,
                            detention: {...colorConfig.detention, critical: e.target.value}
                          })}
                          className="w-full h-10 cursor-pointer"
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}
              
              {/* Content for tab 1 - Card Colors */}
              {configModalTab === 1 && (
                <>
                  <h2 className="font-bold text-lg mb-3">Card Colors</h2>
                  <p className="text-sm text-gray-600 mb-4">
                    Configure the colors used for different card states based on timer statuses.
                  </p>
                  
                  <div className="border-b pb-3 mb-3">
                    <h3 className="font-medium mb-3">Warning States</h3>
                    <div className="grid grid-cols-1 gap-2">
                      {[
                        { key: 'oneWarning', label: 'One Warning:', desc: 'One indicator in warning state' },
                        { key: 'twoWarnings', label: 'Two Warnings:', desc: 'Two indicators in warning state' },
                        { key: 'threeWarnings', label: 'Three Warnings:', desc: 'All indicators in warning state' }
                      ].map(item => (
                        <div key={item.key} className="flex items-center">
                          <label className="w-32 text-sm font-medium">{item.label}</label>
                          <input 
                            type="color" 
                            value={colorConfig.card[item.key as keyof typeof colorConfig.card]}
                            onChange={(e) => setColorConfig({
                              ...colorConfig,
                              card: {
                                ...colorConfig.card, 
                                [item.key]: e.target.value
                              }
                            })}
                            className="w-12 h-8 cursor-pointer mr-3"
                          />
                          <div 
                            className="flex-1 p-2 border rounded-md" 
                            style={{ backgroundColor: colorConfig.card[item.key as keyof typeof colorConfig.card] }}
                          >
                            <span className="text-sm">{item.desc}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="border-b pb-3 mb-3">
                    <h3 className="font-medium mb-3">Critical States</h3>
                    <div className="grid grid-cols-1 gap-2">
                      {[
                        { key: 'oneCritical', label: 'One Critical:', desc: 'One indicator in critical state' },
                        { key: 'twoCriticals', label: 'Two Criticals:', desc: 'Two indicators in critical state' },
                        { key: 'threeCriticals', label: 'Three Criticals:', desc: 'All indicators in critical state' }
                      ].map(item => (
                        <div key={item.key} className="flex items-center">
                          <label className="w-32 text-sm font-medium">{item.label}</label>
                          <input 
                            type="color" 
                            value={colorConfig.card[item.key as keyof typeof colorConfig.card]}
                            onChange={(e) => setColorConfig({
                              ...colorConfig,
                              card: {
                                ...colorConfig.card, 
                                [item.key]: e.target.value
                              }
                            })}
                            className="w-12 h-8 cursor-pointer mr-3"
                          />
                          <div 
                            className="flex-1 p-2 border rounded-md" 
                            style={{ backgroundColor: colorConfig.card[item.key as keyof typeof colorConfig.card] }}
                          >
                            <span className="text-sm">{item.desc}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="font-medium mb-3">Mixed State</h3>
                    <div className="flex items-center">
                      <label className="w-32 text-sm font-medium">Mixed:</label>
                      <input 
                        type="color" 
                        value={colorConfig.card.mixed}
                        onChange={(e) => setColorConfig({
                          ...colorConfig,
                          card: {...colorConfig.card, mixed: e.target.value}
                        })}
                        className="w-12 h-8 cursor-pointer mr-3"
                      />
                      <div className="flex-1 p-2 border rounded-md" style={{ backgroundColor: colorConfig.card.mixed }}>
                        <span className="text-sm">Mix of warning and critical indicators</span>
                      </div>
                    </div>
                  </div>
                </>
              )}
              
              {/* Content for tab 2 - Thresholds */}
              {configModalTab === 2 && (
                <>
                  <h2 className="font-bold text-lg mb-3">Time Thresholds</h2>
                  <p className="text-sm text-gray-600 mb-4">
                    Configure when timers should switch to warning or critical status (in minutes).
                  </p>
                  
                  {/* MDT Thresholds */}
                  <div className="border-b pb-4 mb-4">
                    <h3 className="font-medium mb-3">MDT Thresholds</h3>
                    <div className="grid grid-cols-1 gap-4 mb-2">
                      <div className="bg-blue-50 p-3 rounded text-sm text-blue-800">
                        <strong>Note:</strong> MDT is a countdown timer. Lower values indicate less time remaining.
                        <ul className="list-disc pl-5 mt-1">
                          <li>Warning: When time drops below this threshold</li>
                          <li>Critical: When time drops below this threshold</li>
                        </ul>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">Warning Threshold (minutes)</label>
                        <div className="flex items-center">
                          <input 
                            type="number" 
                            min="1"
                            value={colorConfig.mdt.warningThreshold}
                            onChange={(e) => {
                              const newWarning = parseInt(e.target.value) || 10;
                              setColorConfig({
                                ...colorConfig,
                                mdt: {
                                  ...colorConfig.mdt,
                                  warningThreshold: newWarning
                                }
                              });
                            }}
                            className="w-full p-2 border rounded"
                          />
                          <div 
                            className="ml-2 w-6 h-6 rounded-full" 
                            style={{ backgroundColor: colorConfig.mdt.warning }}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Timer turns yellow when below this many minutes
                        </p>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-1">Critical Threshold (minutes)</label>
                        <div className="flex items-center">
                          <input 
                            type="number" 
                            min="1"
                            value={colorConfig.mdt.criticalThreshold}
                            onChange={(e) => {
                              const value = parseInt(e.target.value) || 5;
                              setColorConfig({
                                ...colorConfig,
                                mdt: {
                                  ...colorConfig.mdt, 
                                  criticalThreshold: value
                                }
                              });
                            }}
                            className="w-full p-2 border rounded"
                          />
                          <div 
                            className="ml-2 w-6 h-6 rounded-full" 
                            style={{ backgroundColor: colorConfig.mdt.critical }}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Timer turns red when below this many minutes
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Dock Timer Thresholds */}
                  <div className="border-b pb-4 mb-4">
                    <h3 className="font-medium mb-3">Dock Timer Thresholds</h3>
                    <div className="grid grid-cols-1 gap-4 mb-2">
                      <div className="bg-blue-50 p-3 rounded text-sm text-blue-800">
                        <strong>Note:</strong> Dock Timer counts up. Higher values indicate more time spent at dock.
                        <ul className="list-disc pl-5 mt-1">
                          <li>Warning: When time exceeds this threshold</li>
                          <li>Critical: When time exceeds this threshold</li>
                        </ul>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">Warning Threshold (minutes)</label>
                        <div className="flex items-center">
                          <input 
                            type="number" 
                            min="1"
                            value={colorConfig.dockTimer.warningThreshold}
                            onChange={(e) => {
                              const value = parseInt(e.target.value) || 10;
                              setColorConfig({
                                ...colorConfig,
                                dockTimer: {
                                  ...colorConfig.dockTimer, 
                                  warningThreshold: value
                                }
                              });
                            }}
                            className="w-full p-2 border rounded"
                          />
                          <div 
                            className="ml-2 w-6 h-6 rounded-full" 
                            style={{ backgroundColor: colorConfig.dockTimer.warning }}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Timer turns yellow when exceeding this many minutes
                        </p>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-1">Critical Threshold (minutes)</label>
                        <div className="flex items-center">
                          <input 
                            type="number" 
                            min="1"
                            value={colorConfig.dockTimer.criticalThreshold}
                            onChange={(e) => {
                              const value = parseInt(e.target.value) || 30;
                              setColorConfig({
                                ...colorConfig,
                                dockTimer: {
                                  ...colorConfig.dockTimer, 
                                  criticalThreshold: value
                                }
                              });
                            }}
                            className="w-full p-2 border rounded"
                          />
                          <div 
                            className="ml-2 w-6 h-6 rounded-full" 
                            style={{ backgroundColor: colorConfig.dockTimer.critical }}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Timer turns red when exceeding this many minutes
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Detention Time Thresholds */}
                  <div>
                    <h3 className="font-medium mb-3">Detention Time Thresholds</h3>
                    <div className="grid grid-cols-1 gap-4 mb-2">
                      <div className="bg-blue-50 p-3 rounded text-sm text-blue-800">
                        <strong>Note:</strong> Detention Timer is a countdown timer. Lower values indicate less time remaining.
                        <ul className="list-disc pl-5 mt-1">
                          <li>Warning: When time drops below this threshold</li>
                          <li>Critical: When time drops below this threshold</li>
                        </ul>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">Warning Threshold (minutes)</label>
                        <div className="flex items-center">
                          <input 
                            type="number" 
                            min="1"
                            value={colorConfig.detention.warningThreshold}
                            onChange={(e) => {
                              const newWarning = parseInt(e.target.value) || 10;
                              setColorConfig({
                                ...colorConfig,
                                detention: {
                                  ...colorConfig.detention,
                                  warningThreshold: newWarning
                                }
                              });
                            }}
                            className="w-full p-2 border rounded"
                          />
                          <div 
                            className="ml-2 w-6 h-6 rounded-full" 
                            style={{ backgroundColor: colorConfig.detention.warning }}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Timer turns yellow when below this many minutes
                        </p>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-1">Critical Threshold (minutes)</label>
                        <div className="flex items-center">
                          <input 
                            type="number" 
                            min="1"
                            value={colorConfig.detention.criticalThreshold}
                            onChange={(e) => {
                              const value = parseInt(e.target.value) || 5;
                              setColorConfig({
                                ...colorConfig,
                                detention: {
                                  ...colorConfig.detention, 
                                  criticalThreshold: value
                                }
                              });
                            }}
                            className="w-full p-2 border rounded"
                          />
                          <div 
                            className="ml-2 w-6 h-6 rounded-full" 
                            style={{ backgroundColor: colorConfig.detention.critical }}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Timer turns red when below this many minutes
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
            
            <div className="mt-5 pt-3 border-t flex justify-end gap-2">
              <button
                className="bg-gray-500 hover:bg-gray-400 text-white p-2 rounded"
                onClick={() => setShowConfigModal(false)}
              >
                Cancel
              </button>
              <button
                className="bg-blue-600 hover:bg-blue-500 text-white p-2 rounded"
                onClick={saveConfiguration}
              >
                Save Configuration
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Dock Status Change Modal */}
      {showDockStatusModal && selectedDock && (
        <Modal
          open={showDockStatusModal}
          onClose={handleDockStatusModalClose}
          className="flex justify-center items-center"
        >
          <div className="w-[500px] bg-white border-2 border-gray-300 shadow-xl rounded-lg p-6 relative flex flex-col max-h-[80vh] overflow-y-auto">
            <Close
              className="absolute top-4 right-4 cursor-pointer hover:text-slate-400 text-gray-600"
              onClick={handleDockStatusModalClose}
            />
            <h1 className="text-xl font-semibold text-gray-800 mb-5">Change Dock {selectedDock.number} Status</h1>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                value={selectedDock.status}
                onChange={(e) => setSelectedDock({ ...selectedDock, status: e.target.value as DockStatus })}
                className="w-full border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-sky-500 focus:border-transparent"
              >
                <option value="available">In-Service</option>
                <option value="unavailable">Unavailable</option>
              </select>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
              <textarea
                value={dockNotesValue}
                onChange={(e) => setDockNotesValue(e.target.value)}
                placeholder="Add notes for this dock door..."
                className="w-full border border-gray-300 rounded-md p-2 h-20 text-sm focus:ring-2 focus:ring-sky-500 focus:border-transparent resize-vertical"
              />
            </div>

            {/* Locks Section */}
            {loadingLocks ? (
              <div className="mb-4 p-3 border border-gray-200 rounded-md">
                <CircularProgress size={20} />
                <span className="ml-2 text-sm text-gray-600">Loading locks...</span>
              </div>
            ) : dockLocks.length > 0 ? (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <Lock fontSize="small" className="text-orange-600" />
                  Active Locks ({dockLocks.length})
                </label>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {dockLocks.map((lock, index) => (
                    <div key={index} className="p-3 bg-orange-50 border border-orange-200 rounded-md">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="text-sm font-medium text-gray-900">{lock.reason || 'Task lock'}</p>
                            {lock.model_name && (
                              <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full">
                                {lock.model_name}
                              </span>
                            )}
                          </div>
                          {lock.resource && (lock.resource.name || lock.resource.trailerNumber) && (
                            <p className="text-xs text-gray-600">Resource: {lock.resource.name || lock.resource.trailerNumber}</p>
                          )}
                          {lock.task_uuid && (
                            <p className="text-xs text-gray-600 mt-1">Task: {lock.task_uuid.substring(0, 8)}...</p>
                          )}
                          {lock.created_at && (
                            <p className="text-xs text-gray-500">Created: {new Date(lock.created_at).toLocaleString()}</p>
                          )}
                        </div>
                        <button
                          onClick={() => handleClearLock(lock.resource_uuid, lock.task_uuid)}
                          disabled={clearingLock}
                          className="ml-2 px-3 py-1 bg-blue-600 hover:bg-blue-500 text-white text-xs rounded flex items-center gap-1 disabled:bg-gray-400"
                        >
                          {clearingLock ? <CircularProgress size={12} color="inherit" /> : <LockOpen fontSize="small" />}
                          Clear
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="flex justify-end gap-2">
              <button
                className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded"
                onClick={handleDockStatusModalClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                className="bg-sky-600 hover:bg-sky-500 text-white px-4 py-2 rounded"
                onClick={handleDockStatusSave}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default WarehouseManagement; 